import {
  ChangeEvent,
  ComponentProps,
  DragEvent,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { LoadingButton } from '@mui/lab';
import { alpha } from '@mui/material/styles';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  ListItemText,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { differenceInCalendarDays, format } from 'date-fns';
import { Page } from '@/components';
import Iconify from '@/components/Iconify';
import useAuth from '@/hooks/useAuth';
import useSettings from '@/hooks/useSettings';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { getDashboardUsers } from '@/features/users/shared/api';
import { User } from '@/features/users/shared/types';
import { ROOT_TASK, ROOTS_TASKS } from '../routes/paths';
import {
  addTaskComment,
  archiveTask,
  createTask,
  deleteTaskAttachment,
  getTaskAttachmentAccessUrl,
  getTaskBoards,
  getTaskSections,
  moveTask,
  updateTask,
  updateTaskComment,
  uploadTaskAttachments,
} from '../api';
import {
  CreateTaskDto,
  MoveTaskDto,
  Task,
  TaskActivity,
  TaskAttachment,
  TaskBoard,
  TaskBoardsResponse,
  TaskComment,
  TaskDashboardUser,
  TaskPriority,
  TaskSectionRef,
  UpdateTaskDto,
} from '../types';
import {
  areTaskAssigneeIdsEqual,
  calculateTaskSortOrder,
  getTaskAssigneeIds,
  getTaskUserDisplayName,
  hasTaskManagerAccess,
  moveTaskInBoards,
  normalizeTaskAssigneeIds,
  patchTaskInBoards,
  removeTaskFromBoards,
  TASK_PRIORITY_OPTIONS,
  toTaskBoardColumns,
} from '../utils';

const TASK_BOARDS_QUERY_KEY = ['tasks', 'boards'];
const TASK_BOARDS_REFRESH_INTERVAL_MS = 60 * 1000;
const TASK_ATTACHMENT_ACCESS_URL_STALE_TIME_MS = 55 * 60 * 1000;
const TASK_ROUTE_REFERENCE_PATTERN = /^gsc-(\d+)$/i;
const TASK_DESCRIPTION_URL_REGEX = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
const TASK_DESCRIPTION_TRAILING_PUNCTUATION_REGEX = /[.,!?;:)\]}]+$/;

const getTaskDisplayReference = (taskId: number) => `GSC-${taskId}`;
const getTaskRouteReference = (taskId: number) => `gsc-${taskId}`;
const getTaskRoutePath = (taskId: number) =>
  `${ROOT_TASK}/${getTaskRouteReference(taskId)}`;

const parseTaskRouteReference = (taskRef?: string) => {
  if (!taskRef) return null;

  const normalizedTaskRef = taskRef.trim();
  const taskId = Number(
    normalizedTaskRef.match(TASK_ROUTE_REFERENCE_PATTERN)?.[1] ??
      normalizedTaskRef
  );

  return Number.isInteger(taskId) && taskId > 0 ? taskId : null;
};

const BOARD_SECTION_REFS: TaskSectionRef[] = [
  'to_do',
  'in_progress',
  'blocked',
  'escalated',
  'done',
];

const SECTION_LABELS: Partial<Record<TaskSectionRef, string>> = {
  to_do: 'To Do',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  escalated: 'Escalated',
  done: 'Done',
};

const SECTION_ACCENTS: Record<
  string,
  { dot: string; border: string; bg: string }
> = {
  to_do: { dot: '#9ca3af', border: 'transparent', bg: '#f3f4f6' },
  in_progress: { dot: '#2563eb', border: 'transparent', bg: '#f3f4f6' },
  blocked: { dot: '#f59e0b', border: '#fed7aa', bg: '#fff7ed' },
  escalated: { dot: '#ef4444', border: '#fecaca', bg: '#fff1f2' },
  done: { dot: '#16a34a', border: 'transparent', bg: '#f3f4f6' },
};

type TaskTagOption = {
  value: string;
  label: string;
  color: string;
  bg: string;
};

const TASK_TAG_MAX_LENGTH = 64;

const TASK_TAG_OPTIONS: TaskTagOption[] = [
  { value: 'cleaner', label: 'Cleaner', color: '#7c3aed', bg: '#f3e8ff' },
  { value: 'client', label: 'Client', color: '#ef4444', bg: '#fee2e2' },
  { value: 'booking', label: 'Booking', color: '#db2777', bg: '#fce7f3' },
  { value: 'issues', label: 'Issues', color: '#22c55e', bg: '#dcfce7' },
  { value: 'system', label: 'System', color: '#0891b2', bg: '#cffafe' },
  { value: 'platform', label: 'Platform', color: '#2563eb', bg: '#dbeafe' },
  { value: 'dashboard', label: 'Dashboard', color: '#4f46e5', bg: '#e0e7ff' },
  {
    value: 'dashboard_users',
    label: 'Dashboard users',
    color: '#9333ea',
    bg: '#f3e8ff',
  },
  {
    value: 'operations',
    label: 'Operations',
    color: '#0f766e',
    bg: '#ccfbf1',
  },
  { value: 'payments', label: 'Payments', color: '#ca8a04', bg: '#fef9c3' },
  {
    value: 'scheduling',
    label: 'Scheduling',
    color: '#ea580c',
    bg: '#ffedd5',
  },
  {
    value: 'notifications',
    label: 'Notifications',
    color: '#be123c',
    bg: '#ffe4e6',
  },
  { value: 'support', label: 'Support', color: '#475569', bg: '#f1f5f9' },
];

const PRIORITY_META: Record<
  TaskPriority,
  { label: string; rank: number; color: string; bg: string }
> = {
  URGENT: { label: 'Urgent', rank: 4, color: '#dc2626', bg: '#fee2e2' },
  HIGH: { label: 'High', rank: 3, color: '#b45309', bg: '#fef3c7' },
  NORMAL: { label: 'Medium', rank: 2, color: '#0369a1', bg: '#e0f2fe' },
  LOW: { label: 'Low', rank: 1, color: '#475569', bg: '#f1f5f9' },
};

const SORT_OPTIONS = [
  { value: 'priority', label: 'Priority' },
  { value: 'due', label: 'Due date' },
  { value: 'title', label: 'Title' },
] as const;

type TaskSortMode = (typeof SORT_OPTIONS)[number]['value'];
type TaskDensity = 'comfortable' | 'compact';
type TaskEditorDialogTab = 'activity' | 'comments';

type CreateDialogState = {
  open: boolean;
  sectionRef?: string;
};

type TaskMutationContext = {
  previous?: TaskBoardsResponse;
};

type MoveTaskMutationVariables = {
  id: number;
  payload: MoveTaskDto;
  skipOptimisticUpdate?: boolean;
  rollbackContext?: TaskMutationContext;
};

type TaskFilters = {
  search: string;
  priorities: TaskPriority[];
  assigneeIds: number[];
  tags: string[];
};

const getErrorMessage = (error: unknown) => {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object') {
    const { message } = error as { message?: unknown };
    if (typeof message === 'string') return message;
  }
  return '';
};

const hasMovePayload = (payload?: MoveTaskDto) =>
  Boolean(
    payload &&
      (payload.section_id !== undefined ||
        payload.section_ref !== undefined ||
        payload.assigned_dashboard_user_ids !== undefined ||
        payload.sort_order !== undefined)
  );

const UNASSIGNED_ASSIGNEE_VALUE = 'unassigned';

const isAssignableDashboardUser = (
  user?: Partial<TaskDashboardUser> | null
): user is TaskDashboardUser => {
  if (!user) return false;

  const id = Number(user.id);
  if (!Number.isFinite(id) || id <= 0) return false;

  return (
    getTaskUserDisplayName(user).trim().toLowerCase() !==
    UNASSIGNED_ASSIGNEE_VALUE
  );
};

const normalizeDashboardUser = (
  user: Partial<User> | Partial<TaskDashboardUser>
): TaskDashboardUser => ({
  id: Number(user.id),
  first_name: user.first_name ?? '',
  last_name: user.last_name ?? '',
  email: user.email ?? '',
  profile_image: user.profile_image ?? null,
  job_title: user.job_title ?? null,
  display_name: (user as Partial<TaskDashboardUser>).display_name ?? null,
});

const buildAssignmentUsers = (
  boards: TaskBoard[],
  dashboardUsers?: User[]
): TaskDashboardUser[] => {
  const usersById = new Map<number, TaskDashboardUser>();

  dashboardUsers?.forEach((user) => {
    const dashboardUser = normalizeDashboardUser(user);
    if (isAssignableDashboardUser(dashboardUser)) {
      usersById.set(dashboardUser.id, dashboardUser);
    }
  });

  if (dashboardUsers === undefined) {
    boards.forEach((board) => {
      const boardUser = board.dashboard_user;
      if (isAssignableDashboardUser(boardUser)) {
        usersById.set(boardUser.id, boardUser);
      }
    });
  }

  return Array.from(usersById.values()).sort((left, right) =>
    getTaskUserDisplayName(left).localeCompare(getTaskUserDisplayName(right))
  );
};

const buildTaskUsersById = (
  boards: TaskBoard[],
  assignmentUsers: TaskDashboardUser[]
): Record<number, TaskDashboardUser> => {
  const usersById = new Map<number, TaskDashboardUser>();

  assignmentUsers.forEach((user) => {
    usersById.set(user.id, user);
  });

  boards.forEach((board) => {
    const boardUser = board.dashboard_user;
    if (isAssignableDashboardUser(boardUser) && !usersById.has(boardUser.id)) {
      usersById.set(boardUser.id, boardUser);
    }
  });

  const users = Array.from(usersById.values());

  return users.reduce<Record<number, TaskDashboardUser>>((acc, user) => {
    acc[user.id] = user;
    return acc;
  }, {});
};

const getAssigneeOptionLabel = (
  user: TaskDashboardUser,
  currentUserId?: number | null
) =>
  user.id === currentUserId
    ? `${getTaskUserDisplayName(user)} (me)`
    : getTaskUserDisplayName(user);

const getFallbackDashboardUser = (id: number): TaskDashboardUser => ({
  id,
  first_name: '',
  last_name: '',
  email: '',
  profile_image: null,
  job_title: null,
  display_name: `Dashboard user #${id}`,
});

const getTaskAssigneeUsers = (
  task: Task,
  usersById: Record<number, TaskDashboardUser>
) =>
  getTaskAssigneeIds(task).map(
    (id) => usersById[id] ?? getFallbackDashboardUser(id)
  );

const dateInputToIso = (value: string) =>
  value ? new Date(`${value}T00:00:00`).toISOString() : null;

const isoToDateInput = (value?: string | null) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
};

const formatShortDate = (value?: string | null) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return format(date, 'MMM d');
};

const getInitials = (user?: Partial<TaskDashboardUser> | null) => {
  const name = getTaskUserDisplayName(user).trim();
  const parts = name.split(/\s+/).filter(Boolean);
  const initials =
    parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : name.slice(0, 2);

  return initials.toUpperCase();
};

const getAvatarColor = (id?: number | null) => {
  const colors = ['#16a34a', '#ea580c', '#2563eb', '#db2777', '#64748b'];
  const index = Math.abs(Number(id ?? 0)) % colors.length;
  return colors[index];
};

const getAvatarSrc = (user?: Partial<TaskDashboardUser> | null) =>
  user?.profile_image?.trim() || undefined;

const normalizeTagText = (value: string) => value.replace(/\s+/g, ' ').trim();

const getTagComparisonKey = (value: string) =>
  normalizeTagText(value).toLowerCase();

const getTaskTagOption = (value: string) => {
  const normalizedValue = getTagComparisonKey(value);
  return TASK_TAG_OPTIONS.find(
    (option) =>
      getTagComparisonKey(option.value) === normalizedValue ||
      getTagComparisonKey(option.label) === normalizedValue
  );
};

const getTaskTags = (task: Task): string[] => {
  const seenTags = new Set<string>();
  const providedTags = (task.tags ?? []).reduce<string[]>((acc, tag) => {
    const normalizedTag = normalizeTagText(String(tag));
    const tagKey = getTagComparisonKey(normalizedTag);
    if (!normalizedTag || seenTags.has(tagKey)) return acc;

    seenTags.add(tagKey);
    acc.push(normalizedTag);
    return acc;
  }, []);

  if (providedTags.length) return providedTags;

  const sourceTag = getTaskTagOption(String(task.source_type ?? ''));
  return sourceTag ? [sourceTag.value] : [];
};

const normalizeTaskTags = (values: string[]) => {
  const seenTags = new Set<string>();

  return values.reduce<string[]>((acc, value) => {
    const normalizedTag = normalizeTagText(value);
    const tagKey = getTagComparisonKey(normalizedTag);
    if (!normalizedTag || seenTags.has(tagKey)) return acc;

    seenTags.add(tagKey);
    acc.push(normalizedTag);
    return acc;
  }, []);
};

const getInvalidTaskTagMessage = (values: string[]) => {
  const hasEmptyTag = values.some((value) => !normalizeTagText(value));
  if (hasEmptyTag) return 'Tags cannot be empty';

  const tooLongTag = values.find(
    (value) => normalizeTagText(value).length > TASK_TAG_MAX_LENGTH
  );
  if (tooLongTag) return 'Tags must be 64 characters or fewer';

  return '';
};

const buildTaskTagOptions = (tasks: Task[]): TaskTagOption[] => {
  const optionsByKey = new Map<string, TaskTagOption>();

  TASK_TAG_OPTIONS.forEach((option) => {
    optionsByKey.set(getTagComparisonKey(option.value), option);
  });

  tasks.flatMap(getTaskTags).forEach((tag) => {
    const tagKey = getTagComparisonKey(tag);
    if (!tagKey || optionsByKey.has(tagKey)) return;

    optionsByKey.set(tagKey, {
      value: tag,
      label: tag,
      color: '#64748b',
      bg: '#f1f5f9',
    });
  });

  return Array.from(optionsByKey.values());
};

const getTaskAttachments = (task: Task) => task.attachments ?? [];

const getTaskCommentTime = (comment: TaskComment) => {
  const timestamp = new Date(comment.created_at).getTime();

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getTaskActivityTime = (activity: TaskActivity) => {
  const timestamp = new Date(activity.created_at).getTime();

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortTaskComments = (comments: TaskComment[] = []) =>
  [...comments].sort(
    (left, right) =>
      getTaskCommentTime(left) - getTaskCommentTime(right) || left.id - right.id
  );

const sortTaskActivity = (activity: TaskActivity[] = []) =>
  [...activity].sort(
    (left, right) =>
      getTaskActivityTime(right) - getTaskActivityTime(left) ||
      right.id - left.id
  );

const appendTaskComment = (
  comments: TaskComment[] | null | undefined,
  comment: TaskComment
) =>
  sortTaskComments([
    ...(comments ?? []).filter((item) => item.id !== comment.id),
    comment,
  ]);

const taskActivityFromComment = (comment: TaskComment): TaskActivity => ({
  id: comment.id,
  task_id: comment.task_id,
  organisation_id: comment.organisation_id,
  actor_dashboard_user_id: comment.actor_dashboard_user_id,
  action: comment.action ?? 'comment',
  comment: comment.comment,
  from_section_id: comment.from_section_id ?? null,
  to_section_id: comment.to_section_id ?? null,
  from_assigned_dashboard_user_id:
    comment.from_assigned_dashboard_user_id ?? null,
  to_assigned_dashboard_user_id: comment.to_assigned_dashboard_user_id ?? null,
  old_value: comment.old_value,
  new_value: comment.new_value,
  created_at: comment.created_at,
});

const appendTaskActivity = (
  activity: TaskActivity[] | null | undefined,
  nextActivity: TaskActivity
) =>
  sortTaskActivity([
    ...(activity ?? []).filter((item) => item.id !== nextActivity.id),
    nextActivity,
  ]);

const appendTaskCommentToBoards = (
  response: TaskBoardsResponse | undefined,
  taskId: number,
  comment: TaskComment
): TaskBoardsResponse | undefined => {
  if (!response) return response;

  return {
    ...response,
    data: response.data.map((board) => ({
      ...board,
      sections: board.sections.map((section) => ({
        ...section,
        tasks: (section.tasks ?? []).map((task) =>
          task.id === taskId
            ? {
                ...task,
                comments: appendTaskComment(task.comments, comment),
                activity: appendTaskActivity(
                  task.activity,
                  taskActivityFromComment(comment)
                ),
              }
            : task
        ),
      })),
    })),
  };
};

const getTaskCommentAuthorName = (
  comment: TaskComment,
  usersById: Record<number, TaskDashboardUser>
) => {
  const user = usersById[comment.actor_dashboard_user_id];

  return user
    ? getTaskUserDisplayName(user)
    : `Dashboard user #${comment.actor_dashboard_user_id}`;
};

const formatTaskCommentCreatedAt = (comment: TaskComment) => {
  const date = new Date(comment.created_at);

  return Number.isNaN(date.getTime())
    ? 'Unknown time'
    : format(date, 'MMM d, p');
};

const formatTaskActivityCreatedAt = (activity: TaskActivity) => {
  const date = new Date(activity.created_at);

  return Number.isNaN(date.getTime())
    ? 'Unknown time'
    : format(date, 'MMM d, p');
};

const getTaskActivityAuthorName = (
  activity: TaskActivity,
  usersById: Record<number, TaskDashboardUser>
) => {
  const user = usersById[activity.actor_dashboard_user_id];

  return user
    ? getTaskUserDisplayName(user)
    : `Dashboard user #${activity.actor_dashboard_user_id}`;
};

const buildTaskSectionNamesById = (
  sections: ReturnType<typeof toTaskBoardColumns>
) =>
  sections.reduce<Record<number, string>>((acc, section) => {
    acc[section.id] = acc[section.id] ?? section.name;
    return acc;
  }, {});

const getTaskActivitySectionName = (
  sectionId: number | null | undefined,
  sectionsById: Record<number, string>
) => {
  if (sectionId == null) return 'No status';

  return sectionsById[sectionId] ?? `Section #${sectionId}`;
};

const getTaskActivityAssigneeName = (
  dashboardUserId: number | null | undefined,
  usersById: Record<number, TaskDashboardUser>
) => {
  if (dashboardUserId == null || dashboardUserId === 0) return 'Unassigned';

  const user = usersById[dashboardUserId];
  return user
    ? getTaskUserDisplayName(user)
    : `Dashboard user #${dashboardUserId}`;
};

const getTaskActivityAssigneeNames = (
  value: unknown,
  usersById: Record<number, TaskDashboardUser>
) => {
  const assigneeIds = Array.isArray(value)
    ? normalizeTaskAssigneeIds(value)
    : normalizeTaskAssigneeIds([toTaskActivityNumber(value)]);

  if (!assigneeIds.length) return 'Unassigned';

  return assigneeIds
    .map((id) => getTaskActivityAssigneeName(id, usersById))
    .join(', ');
};

const isPlainTaskActivityValue = (
  value: unknown
): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const getTaskActivityValue = (value: unknown, key: string) =>
  isPlainTaskActivityValue(value) ? value[key] : undefined;

const toTaskActivityNumber = (value: unknown) => {
  if (value === null || value === undefined || value === '') return null;

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const stringifyTaskActivityValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return 'None';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'None';
  if (typeof value === 'string' || typeof value === 'number')
    return String(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  return 'Changed';
};

const isSameTaskActivityValue = (left: unknown, right: unknown) => {
  if (Object.is(left, right)) return true;

  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
};

const formatTaskActivityPriority = (value: unknown) => {
  if (typeof value !== 'string') return stringifyTaskActivityValue(value);

  return PRIORITY_META[value as TaskPriority]?.label ?? value;
};

const formatTaskActivityDueDate = (value: unknown) => {
  if (typeof value !== 'string' || !value) return 'None';

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : format(date, 'MMM d, yyyy');
};

const getTaskActivityChangedDetails = (
  activity: TaskActivity,
  sectionsById: Record<number, string>,
  usersById: Record<number, TaskDashboardUser>
) => {
  const fields: Array<{
    key: string;
    label: string;
    formatter?: (value: unknown) => string;
  }> = [
    { key: 'title', label: 'Title' },
    { key: 'description', label: 'Description' },
    {
      key: 'section_id',
      label: 'Status',
      formatter: (value) =>
        getTaskActivitySectionName(toTaskActivityNumber(value), sectionsById),
    },
    {
      key: 'assigned_dashboard_user_ids',
      label: 'Assignees',
      formatter: (value) => getTaskActivityAssigneeNames(value, usersById),
    },
    {
      key: 'assigned_dashboard_user_id',
      label: 'Assignee',
      formatter: (value) =>
        getTaskActivityAssigneeName(toTaskActivityNumber(value), usersById),
    },
    {
      key: 'priority',
      label: 'Priority',
      formatter: formatTaskActivityPriority,
    },
    { key: 'due_at', label: 'Due date', formatter: formatTaskActivityDueDate },
    { key: 'tags', label: 'Tags' },
  ];

  return fields
    .map((field) => {
      const oldValue = getTaskActivityValue(activity.old_value, field.key);
      const newValue = getTaskActivityValue(activity.new_value, field.key);
      if (isSameTaskActivityValue(oldValue, newValue)) return null;

      if (field.key === 'title' || field.key === 'description') {
        return `${field.label} changed`;
      }

      const formatValue = field.formatter ?? stringifyTaskActivityValue;
      return `${field.label}: ${formatValue(oldValue)} to ${formatValue(
        newValue
      )}`;
    })
    .filter((detail): detail is string => Boolean(detail));
};

const getTaskActivityCreationDetails = (
  activity: TaskActivity,
  sectionsById: Record<number, string>,
  usersById: Record<number, TaskDashboardUser>
) => {
  const newValue = activity.new_value;
  const sectionId =
    getTaskActivityValue(newValue, 'section_id') ?? activity.to_section_id;
  const assigneeId =
    getTaskActivityValue(newValue, 'assigned_dashboard_user_ids') ??
    getTaskActivityValue(newValue, 'assigned_dashboard_user_id') ??
    activity.to_assigned_dashboard_user_id;
  const priority = getTaskActivityValue(newValue, 'priority');
  const dueAt = getTaskActivityValue(newValue, 'due_at');

  return [
    `Status: ${getTaskActivitySectionName(
      toTaskActivityNumber(sectionId),
      sectionsById
    )}`,
    `Assignee: ${getTaskActivityAssigneeNames(assigneeId, usersById)}`,
    priority ? `Priority: ${formatTaskActivityPriority(priority)}` : null,
    dueAt ? `Due date: ${formatTaskActivityDueDate(dueAt)}` : null,
  ].filter((detail): detail is string => Boolean(detail));
};

const getTaskActivityAssigneeDetails = (
  activity: TaskActivity,
  usersById: Record<number, TaskDashboardUser>
) => {
  const fromAssignee = activity.from_assigned_dashboard_user_id;
  const toAssignee = activity.to_assigned_dashboard_user_id;

  if (fromAssignee === toAssignee) return null;

  return `Assignee: ${getTaskActivityAssigneeName(
    fromAssignee,
    usersById
  )} to ${getTaskActivityAssigneeName(toAssignee, usersById)}`;
};

const getTaskActivityPresentation = (
  activity: TaskActivity,
  sectionsById: Record<number, string>,
  usersById: Record<number, TaskDashboardUser>
) => {
  const action = (activity.action || 'activity').toLowerCase();
  const fromSection = getTaskActivitySectionName(
    activity.from_section_id,
    sectionsById
  );
  const toSection = getTaskActivitySectionName(
    activity.to_section_id,
    sectionsById
  );
  const sectionChanged =
    activity.from_section_id !== activity.to_section_id &&
    activity.to_section_id != null;
  const assigneeDetail = getTaskActivityAssigneeDetails(activity, usersById);

  if (action === 'create') {
    return {
      title: 'created this task',
      details: getTaskActivityCreationDetails(
        activity,
        sectionsById,
        usersById
      ),
      comment: null,
    };
  }

  if (action === 'comment') {
    return {
      title: 'commented',
      details: [],
      comment: activity.comment,
    };
  }

  if (action === 'complete') {
    return {
      title: 'completed this task',
      details: sectionChanged ? [`Status: ${fromSection} to ${toSection}`] : [],
      comment: null,
    };
  }

  if (action === 'move') {
    return {
      title: sectionChanged
        ? `moved this task from ${fromSection} to ${toSection}`
        : 'moved this task',
      details: assigneeDetail ? [assigneeDetail] : [],
      comment: null,
    };
  }

  if (assigneeDetail && !sectionChanged) {
    return {
      title: 'reassigned this task',
      details: [assigneeDetail],
      comment: null,
    };
  }

  if (action === 'update') {
    return {
      title: 'updated this task',
      details: getTaskActivityChangedDetails(activity, sectionsById, usersById),
      comment: null,
    };
  }

  const actionTitleByAction: Record<string, string> = {
    archive: 'archived this task',
    reopen: 'reopened this task',
    restore: 'restored this task',
    delete: 'deleted this task',
  };

  return {
    title:
      actionTitleByAction[action] ?? `${action.replace(/_/g, ' ')} this task`,
    details: [
      ...(sectionChanged ? [`Status: ${fromSection} to ${toSection}`] : []),
      ...(assigneeDetail ? [assigneeDetail] : []),
    ],
    comment: activity.comment,
  };
};

const getActiveCommentMention = (value: string, caretIndex: number) => {
  const safeCaretIndex = Math.min(Math.max(caretIndex, 0), value.length);
  const textBeforeCaret = value.slice(0, safeCaretIndex);
  const match = textBeforeCaret.match(/(^|\s)@([^\s@]*)$/);
  if (!match) return null;

  const query = match[2] ?? '';

  return {
    start: safeCaretIndex - query.length - 1,
    end: safeCaretIndex,
    query: query.toLowerCase(),
  };
};

type ActiveCommentMention = NonNullable<
  ReturnType<typeof getActiveCommentMention>
>;

const filterMentionUsers = (
  users: TaskDashboardUser[],
  activeMention: ActiveCommentMention | null
) => {
  if (!activeMention) return [];

  const { query } = activeMention;

  return users
    .filter((user) => {
      const searchableText = [
        getTaskUserDisplayName(user),
        user.email,
        user.job_title,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return !query || searchableText.includes(query);
    })
    .slice(0, 6);
};

const escapeRegex = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getCommentMentionParts = (
  text: string,
  users: TaskDashboardUser[]
): Array<{ mention: boolean; text: string }> => {
  const mentionTexts = Array.from(
    new Set(
      users
        .map((user) => `@${getTaskUserDisplayName(user).trim()}`)
        .filter((mentionText) => mentionText.length > 1)
    )
  ).sort((left, right) => right.length - left.length);

  if (!mentionTexts.length) return [{ mention: false, text }];

  const mentionRegex = new RegExp(
    mentionTexts.map(escapeRegex).join('|'),
    'gi'
  );
  const parts: Array<{ mention: boolean; text: string }> = [];
  let previousIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = mentionRegex.exec(text)) !== null) {
    const [mentionText] = match;

    if (match.index > previousIndex) {
      parts.push({
        mention: false,
        text: text.slice(previousIndex, match.index),
      });
    }

    parts.push({ mention: true, text: mentionText });
    previousIndex = match.index + mentionText.length;
  }

  if (previousIndex < text.length) {
    parts.push({ mention: false, text: text.slice(previousIndex) });
  }

  return parts.length ? parts : [{ mention: false, text }];
};

const IMAGE_ATTACHMENT_EXTENSION_PATTERN =
  /\.(apng|avif|bmp|gif|jpe?g|png|svg|webp)(?:\?|#|$)/i;

const getSafeTaskDescriptionHref = (url: string) => {
  const href = url.startsWith('www.') ? `https://${url}` : url;

  try {
    const parsedUrl = new URL(href);
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
      ? href
      : null;
  } catch {
    return null;
  }
};

const getTaskDescriptionLinks = (text: string) => {
  const links: Array<{ href: string; start: number; text: string }> = [];
  const seenHrefs = new Set<string>();
  let match: RegExpExecArray | null;

  TASK_DESCRIPTION_URL_REGEX.lastIndex = 0;

  while ((match = TASK_DESCRIPTION_URL_REGEX.exec(text)) !== null) {
    const [matchedUrl] = match;
    const trailingPunctuation =
      matchedUrl.match(TASK_DESCRIPTION_TRAILING_PUNCTUATION_REGEX)?.[0] ?? '';
    const urlText = trailingPunctuation
      ? matchedUrl.slice(0, -trailingPunctuation.length)
      : matchedUrl;
    const href = getSafeTaskDescriptionHref(urlText);

    if (href && !seenHrefs.has(href)) {
      links.push({ href, start: match.index, text: urlText });
      seenHrefs.add(href);
    }
  }

  return links;
};

const getStoredAttachmentUrl = (attachment: TaskAttachment) =>
  attachment.url?.trim() || '';

const isSafeAttachmentAccessUrl = (url: unknown): url is string => {
  if (typeof url !== 'string' || !url.trim()) return false;

  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
  } catch {
    return false;
  }
};

const isImageAttachment = (attachment: TaskAttachment) => {
  const mimeType = attachment.mime_type?.toLowerCase() ?? '';

  return (
    mimeType.startsWith('image/') ||
    IMAGE_ATTACHMENT_EXTENSION_PATTERN.test(attachment.name) ||
    IMAGE_ATTACHMENT_EXTENSION_PATTERN.test(getStoredAttachmentUrl(attachment))
  );
};

const getTagMeta = (value: string) =>
  getTaskTagOption(value) ?? {
    value,
    label: value,
    color: '#64748b',
    bg: '#f1f5f9',
  };

const getDuePresentation = (task: Task, now = new Date()) => {
  if (task.completed_at) {
    return {
      label: `Done ${formatShortDate(task.completed_at) ?? ''}`.trim(),
      tone: 'done' as const,
    };
  }

  if (!task.due_at) {
    return { label: 'No due date', tone: 'none' as const };
  }

  const dueDate = new Date(task.due_at);
  if (Number.isNaN(dueDate.getTime())) {
    return { label: 'No due date', tone: 'none' as const };
  }

  const dayDiff = differenceInCalendarDays(dueDate, now);
  if (dayDiff < 0) {
    return { label: `Overdue ${Math.abs(dayDiff)}d`, tone: 'overdue' as const };
  }

  if (dayDiff === 0) {
    return { label: 'Due today', tone: 'today' as const };
  }

  return { label: format(dueDate, 'MMM d'), tone: 'upcoming' as const };
};

const matchesTaskFilters = (
  task: Task,
  usersById: Record<number, TaskDashboardUser>,
  filters: TaskFilters
) => {
  const tags = getTaskTags(task);
  const assignees = getTaskAssigneeUsers(task, usersById);
  const search = filters.search.trim().toLowerCase();

  if (search) {
    const searchableText = [
      getTaskRouteReference(task.id),
      task.title,
      task.description,
      ...assignees.map((assignee) => getTaskUserDisplayName(assignee)),
      ...tags,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (!searchableText.includes(search)) return false;
  }

  if (
    filters.priorities.length &&
    !filters.priorities.includes(task.priority)
  ) {
    return false;
  }

  if (
    filters.assigneeIds.length &&
    !filters.assigneeIds.some((id) => getTaskAssigneeIds(task).includes(id))
  ) {
    return false;
  }

  if (
    filters.tags.length &&
    !filters.tags.some((tag) =>
      tags.some(
        (taskTag) => getTagComparisonKey(taskTag) === getTagComparisonKey(tag)
      )
    )
  ) {
    return false;
  }

  return true;
};

const sortTasksForBoard = (tasks: Task[], sortMode: TaskSortMode) =>
  [...tasks].sort((left, right) => {
    if (sortMode === 'priority') {
      return (
        PRIORITY_META[right.priority].rank -
          PRIORITY_META[left.priority].rank ||
        left.sort_order - right.sort_order ||
        left.id - right.id
      );
    }

    if (sortMode === 'due') {
      const leftTime = left.due_at ? new Date(left.due_at).getTime() : Infinity;
      const rightTime = right.due_at
        ? new Date(right.due_at).getTime()
        : Infinity;

      return leftTime - rightTime || left.sort_order - right.sort_order;
    }

    return left.title.localeCompare(right.title) || left.id - right.id;
  });

const buildDisplayColumns = (
  columns: ReturnType<typeof toTaskBoardColumns>,
  filters: TaskFilters,
  sortMode: TaskSortMode,
  usersById: Record<number, TaskDashboardUser>
) => {
  const columnsByRef = columns.reduce<Record<string, (typeof columns)[number]>>(
    (acc, column) => {
      acc[column.ref] = column;
      return acc;
    },
    {}
  );

  return BOARD_SECTION_REFS.map((targetRef) => {
    const sourceRefs =
      targetRef === 'to_do' ? ['triage', 'to_do'] : [targetRef];
    const sourceColumns = sourceRefs
      .map((ref) => columnsByRef[ref])
      .filter(Boolean);
    const baseColumn =
      columnsByRef[targetRef] ?? sourceColumns[0] ?? columns[0] ?? null;
    const sourceTasks = sourceColumns.flatMap((column) => column.tasks ?? []);
    const tasks = sortTasksForBoard(
      sourceTasks.filter((task) =>
        matchesTaskFilters(task, usersById, filters)
      ),
      sortMode
    );

    return {
      ...(baseColumn ?? {
        id: -(BOARD_SECTION_REFS.indexOf(targetRef) + 1),
        organisation_id: 0,
        position: 0,
        terminal: targetRef === 'done',
        active: true,
      }),
      ref: targetRef,
      name: SECTION_LABELS[targetRef] ?? baseColumn?.name ?? targetRef,
      tasks,
      count: tasks.length,
    };
  });
};

const getColumnByRef = (
  columns: ReturnType<typeof toTaskBoardColumns>,
  ref: string
) =>
  columns.find((section) => section.ref === ref) ??
  (ref === 'to_do'
    ? columns.find((section) => section.ref === 'triage')
    : undefined);

const toggleArrayValue = <T,>(values: T[], value: T) =>
  values.includes(value)
    ? values.filter((item) => item !== value)
    : [...values, value];

export default function TasksDashboard() {
  const { themeStretch } = useSettings();
  const { permissions, isSuperAdmin, user } = useAuth();
  const { errorMsg, successMsg } = useSnackbarMsg();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { taskRef } = useParams<{ taskRef?: string }>();
  const routeTaskId = useMemo(
    () => parseTaskRouteReference(taskRef),
    [taskRef]
  );

  const canEditAllTaskComments = hasTaskManagerAccess(
    isSuperAdmin,
    permissions
  );
  const canAssignTasks = true;
  const currentBoardsQueryKey = TASK_BOARDS_QUERY_KEY;
  const currentUserId = Number((user as { id?: unknown } | null)?.id);
  const currentDashboardUserId =
    Number.isFinite(currentUserId) && currentUserId > 0 ? currentUserId : null;

  const [createDialog, setCreateDialog] = useState<CreateDialogState>({
    open: false,
  });
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [selectedTaskSnapshot, setSelectedTaskSnapshot] = useState<Task | null>(
    null
  );
  const [pendingMoveTaskIds, setPendingMoveTaskIds] = useState<number[]>([]);
  const [search, setSearch] = useState('');
  const [selectedPriorities, setSelectedPriorities] = useState<TaskPriority[]>(
    []
  );
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<number[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sortMode, setSortMode] = useState<TaskSortMode>('priority');
  const [density, setDensity] = useState<TaskDensity>('comfortable');

  const boardsQuery = useQuery({
    queryKey: currentBoardsQueryKey,
    queryFn: () => getTaskBoards({ all: true }),
    ...(process.env.NODE_ENV === 'test'
      ? {}
      : { refetchInterval: TASK_BOARDS_REFRESH_INTERVAL_MS }),
    onError: (error) =>
      errorMsg(error, 'There was an error while fetching task boards'),
  });

  const dashboardUsersQuery = useQuery({
    queryKey: ['dashboardUsers', 'tasks-assignment', 'all'],
    queryFn: getDashboardUsers,
    onError: (error) =>
      errorMsg(error, 'There was an error while fetching dashboard users'),
  });

  const sectionsQuery = useQuery({
    queryKey: ['tasks', 'sections'],
    queryFn: getTaskSections,
    staleTime: 24 * 60 * 60 * 1000,
    onError: (error) =>
      errorMsg(error, 'There was an error while fetching task sections'),
  });

  const boards = useMemo(
    () => boardsQuery.data?.data ?? [],
    [boardsQuery.data]
  );
  const assignmentUsers = useMemo(
    () => buildAssignmentUsers(boards, dashboardUsersQuery.data),
    [boards, dashboardUsersQuery.data]
  );
  const organisationBoard = useMemo(() => boards[0] ?? null, [boards]);
  const usersById = useMemo(
    () => buildTaskUsersById(boards, assignmentUsers),
    [assignmentUsers, boards]
  );

  const boardColumns = useMemo(
    () => toTaskBoardColumns(organisationBoard, sectionsQuery.data),
    [organisationBoard, sectionsQuery.data]
  );
  const filters = useMemo<TaskFilters>(
    () => ({
      search,
      priorities: selectedPriorities,
      assigneeIds: selectedAssigneeIds,
      tags: selectedTags,
    }),
    [search, selectedAssigneeIds, selectedPriorities, selectedTags]
  );
  const displayColumns = useMemo(
    () => buildDisplayColumns(boardColumns, filters, sortMode, usersById),
    [boardColumns, filters, sortMode, usersById]
  );
  const unfilteredDisplayColumns = useMemo(
    () =>
      buildDisplayColumns(
        boardColumns,
        { search: '', priorities: [], assigneeIds: [], tags: [] },
        'priority',
        usersById
      ),
    [boardColumns, usersById]
  );
  const allTasks = useMemo(
    () => unfilteredDisplayColumns.flatMap((column) => column.tasks),
    [unfilteredDisplayColumns]
  );
  const visibleTaskCount = useMemo(
    () =>
      displayColumns.reduce((total, column) => total + column.tasks.length, 0),
    [displayColumns]
  );
  const selectedTaskFromBoards = useMemo(
    () =>
      selectedTaskId
        ? allTasks.find((task) => task.id === selectedTaskId) ?? null
        : null,
    [allTasks, selectedTaskId]
  );
  const selectedTask = selectedTaskFromBoards ?? selectedTaskSnapshot;

  useEffect(() => {
    if (!selectedTaskId) {
      setSelectedTaskSnapshot(null);
      return;
    }

    if (selectedTaskFromBoards) {
      setSelectedTaskSnapshot(selectedTaskFromBoards);
    }
  }, [selectedTaskFromBoards, selectedTaskId]);

  useEffect(() => {
    if (routeTaskId !== null) {
      setSelectedTaskId((currentTaskId) =>
        currentTaskId === routeTaskId ? currentTaskId : routeTaskId
      );
      return;
    }

    if (taskRef || location.pathname === ROOTS_TASKS) {
      setSelectedTaskId(null);
      setSelectedTaskSnapshot(null);
    }
  }, [location.pathname, routeTaskId, taskRef]);

  const urgentTaskCount = useMemo(
    () => allTasks.filter((task) => task.priority === 'URGENT').length,
    [allTasks]
  );
  const availableTags = useMemo(
    () => buildTaskTagOptions(allTasks),
    [allTasks]
  );

  const updateCachedBoards = (
    updater: (
      old: TaskBoardsResponse | undefined
    ) => TaskBoardsResponse | undefined
  ) => {
    queryClient.setQueryData<TaskBoardsResponse>(
      currentBoardsQueryKey,
      updater
    );
  };

  const invalidateTaskBoards = () => {
    queryClient.invalidateQueries(TASK_BOARDS_QUERY_KEY);
  };

  const addTaskCommentMutation = useMutation({
    mutationFn: ({ id, comment }: { id: number; comment: string }) =>
      addTaskComment(id, { comment }),
    onSuccess: (comment, variables) => {
      updateCachedBoards((old) =>
        appendTaskCommentToBoards(old, variables.id, comment)
      );
      setSelectedTaskSnapshot((previousTask) =>
        previousTask?.id === variables.id
          ? {
              ...previousTask,
              comments: appendTaskComment(previousTask.comments, comment),
              activity: appendTaskActivity(
                previousTask.activity,
                taskActivityFromComment(comment)
              ),
            }
          : previousTask
      );
    },
    onSettled: invalidateTaskBoards,
  });

  const updateTaskCommentMutation = useMutation({
    mutationFn: ({
      id,
      commentId,
      comment,
    }: {
      id: number;
      commentId: number;
      comment: string;
    }) => updateTaskComment(id, commentId, { comment }),
    onSuccess: (comment, variables) => {
      updateCachedBoards((old) =>
        appendTaskCommentToBoards(old, variables.id, comment)
      );
      setSelectedTaskSnapshot((previousTask) =>
        previousTask?.id === variables.id
          ? {
              ...previousTask,
              comments: appendTaskComment(previousTask.comments, comment),
              activity: appendTaskActivity(
                previousTask.activity,
                taskActivityFromComment(comment)
              ),
            }
          : previousTask
      );
    },
    onSettled: invalidateTaskBoards,
  });

  const handleOpenTask = (task: Task) => {
    addTaskCommentMutation.reset();
    updateTaskCommentMutation.reset();
    setSelectedTaskSnapshot(task);
    setSelectedTaskId(task.id);
    navigate(getTaskRoutePath(task.id));
  };

  const handleCloseTask = () => {
    addTaskCommentMutation.reset();
    updateTaskCommentMutation.reset();
    setSelectedTaskId(null);
    setSelectedTaskSnapshot(null);

    if (location.pathname !== ROOTS_TASKS) {
      navigate(ROOTS_TASKS, { replace: true });
    }
  };

  const moveTaskMutation = useMutation({
    mutationFn: ({ id, payload }: MoveTaskMutationVariables) =>
      moveTask(id, payload),
    onMutate: async ({
      id,
      payload,
      skipOptimisticUpdate,
      rollbackContext,
    }) => {
      await queryClient.cancelQueries(currentBoardsQueryKey);
      if (skipOptimisticUpdate) {
        return rollbackContext ?? ({} as TaskMutationContext);
      }

      const previous = queryClient.getQueryData<TaskBoardsResponse>(
        currentBoardsQueryKey
      );
      updateCachedBoards((old) => moveTaskInBoards(old, id, payload));

      return { previous } as TaskMutationContext;
    },
    onSuccess: (task, variables) => {
      updateCachedBoards((old) =>
        moveTaskInBoards(old, variables.id, variables.payload, task)
      );
    },
    onError: (error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(currentBoardsQueryKey, context.previous);
      }
      errorMsg(error, 'There was an error while moving the task');
    },
    onSettled: (_task, _error, variables) => {
      if (variables?.skipOptimisticUpdate) {
        setPendingMoveTaskIds((prev) => {
          const taskIndex = prev.indexOf(variables.id);
          if (taskIndex === -1) return prev;

          return [...prev.slice(0, taskIndex), ...prev.slice(taskIndex + 1)];
        });
      }

      invalidateTaskBoards();
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateTaskDto }) =>
      updateTask(id, payload),
    onMutate: async ({ id, payload }) => {
      await queryClient.cancelQueries(currentBoardsQueryKey);
      const previous = queryClient.getQueryData<TaskBoardsResponse>(
        currentBoardsQueryKey
      );
      updateCachedBoards((old) => patchTaskInBoards(old, id, payload));

      return { previous } as TaskMutationContext;
    },
    onSuccess: (task, variables) => {
      updateCachedBoards((old) => patchTaskInBoards(old, variables.id, task));
    },
    onError: (error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(currentBoardsQueryKey, context.previous);
      }
      errorMsg(error, 'There was an error while updating the task');
    },
    onSettled: invalidateTaskBoards,
  });

  const archiveTaskMutation = useMutation({
    mutationFn: (id: number) => archiveTask(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries(currentBoardsQueryKey);
      const previous = queryClient.getQueryData<TaskBoardsResponse>(
        currentBoardsQueryKey
      );
      updateCachedBoards((old) => removeTaskFromBoards(old, id));

      return { previous } as TaskMutationContext;
    },
    onSuccess: () => {
      handleCloseTask();
      successMsg('Task archived');
    },
    onError: (error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(currentBoardsQueryKey, context.previous);
      }
      errorMsg(error, 'There was an error while archiving the task');
    },
    onSettled: invalidateTaskBoards,
  });

  const uploadTaskAttachmentsMutation = useMutation({
    mutationFn: ({ id, files }: { id: number; files: File[] }) =>
      uploadTaskAttachments(id, files),
    onSuccess: (result, variables) => {
      if (Array.isArray(result)) {
        updateCachedBoards((old) =>
          patchTaskInBoards(old, variables.id, { attachments: result })
        );
      } else {
        updateCachedBoards((old) =>
          patchTaskInBoards(old, variables.id, result)
        );
      }
      successMsg('Attachments uploaded');
    },
    onError: (error) =>
      errorMsg(error, 'There was an error while uploading attachments'),
    onSettled: invalidateTaskBoards,
  });

  const deleteTaskAttachmentMutation = useMutation({
    mutationFn: ({
      id,
      attachmentId,
    }: {
      id: number;
      attachmentId: string | number;
    }) => deleteTaskAttachment(id, attachmentId),
    onSuccess: (_data, variables) => {
      const currentTask = allTasks.find((task) => task.id === variables.id);

      updateCachedBoards((old) =>
        patchTaskInBoards(old, variables.id, {
          attachments: (currentTask?.attachments ?? []).filter(
            (attachment) => attachment.id !== variables.attachmentId
          ),
        })
      );
      successMsg('Attachment deleted');
    },
    onError: (error) =>
      errorMsg(error, 'There was an error while deleting the attachment'),
    onSettled: invalidateTaskBoards,
  });

  const createTaskMutation = useMutation({
    mutationFn: createTask,
    onSuccess: () => {
      successMsg('Task created');
      invalidateTaskBoards();
    },
    onError: (error) =>
      errorMsg(error, 'There was an error while creating task'),
  });

  const handleCreateTask = async (
    payload: CreateTaskDto,
    attachmentFiles: File[] = []
  ) => {
    const createdTask = await createTaskMutation.mutateAsync(payload);

    if (attachmentFiles.length) {
      await uploadTaskAttachmentsMutation.mutateAsync({
        id: createdTask.id,
        files: attachmentFiles,
      });
    }

    setCreateDialog({ open: false });
    invalidateTaskBoards();
  };

  const handleSaveTask = async (
    task: Task,
    detailsPayload: UpdateTaskDto,
    movePayload?: MoveTaskDto
  ) => {
    await updateTaskMutation.mutateAsync({
      id: task.id,
      payload: detailsPayload,
    });

    if (hasMovePayload(movePayload)) {
      await moveTaskMutation.mutateAsync({
        id: task.id,
        payload: movePayload as MoveTaskDto,
      });
    }

    handleCloseTask();
    successMsg('Task updated');
  };

  const handleUploadTaskAttachments = async (task: Task, files: File[]) => {
    if (!files.length) return;

    await uploadTaskAttachmentsMutation.mutateAsync({
      id: task.id,
      files,
    });
  };

  const handleDeleteTaskAttachment = async (
    task: Task,
    attachmentId: string | number
  ) => {
    await deleteTaskAttachmentMutation.mutateAsync({
      id: task.id,
      attachmentId,
    });
  };

  const handleAddTaskComment = async (task: Task, comment: string) => {
    const trimmedComment = comment.trim();
    if (!trimmedComment) return;

    await addTaskCommentMutation.mutateAsync({
      id: task.id,
      comment: trimmedComment,
    });
  };

  const handleUpdateTaskComment = async (
    task: Task,
    comment: TaskComment,
    nextComment: string
  ) => {
    const trimmedComment = nextComment.trim();
    if (!trimmedComment) return;

    await updateTaskCommentMutation.mutateAsync({
      id: task.id,
      commentId: comment.id,
      comment: trimmedComment,
    });
  };

  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId, type } = result;

    if (!destination || type !== 'task' || !organisationBoard) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const destinationSection = unfilteredDisplayColumns.find(
      (section) => section.ref === destination.droppableId
    );

    if (!destinationSection) return;

    const taskId = Number(draggableId);
    if (!Number.isFinite(taskId)) return;

    const movingTask = allTasks.find((task) => task.id === taskId);
    if (!movingTask) return;

    const sortOrder = calculateTaskSortOrder(
      destinationSection.tasks,
      destination.index,
      draggableId
    );
    const previous = queryClient.getQueryData<TaskBoardsResponse>(
      currentBoardsQueryKey
    );
    const payload: MoveTaskDto = {
      section_ref: destinationSection.ref,
      assigned_dashboard_user_ids: getTaskAssigneeIds(movingTask),
      sort_order: sortOrder,
    };
    const rollbackContext: TaskMutationContext = { previous };

    setPendingMoveTaskIds((prev) => [...prev, taskId]);
    updateCachedBoards((old) => moveTaskInBoards(old, taskId, payload));

    moveTaskMutation.mutate({
      id: taskId,
      payload,
      skipOptimisticUpdate: true,
      rollbackContext,
    });
  };

  const isInitialLoading = boardsQuery.isLoading && !boardsQuery.data;
  const canCreateTask =
    !isInitialLoading && Boolean(organisationBoard || boardColumns.length);

  return (
    <Page title="Tasks" sx={{ minHeight: 1, bgcolor: '#f8fafc' }}>
      <Container
        maxWidth={themeStretch ? false : 'xl'}
        sx={{ py: 3, px: { xs: 2, md: 3 } }}
      >
        <Stack spacing={2.5}>
          <TaskPageHeader
            totalTasks={allTasks.length}
            visibleTasks={visibleTaskCount}
            urgentTasks={urgentTaskCount}
            onCreateTask={() =>
              setCreateDialog({ open: true, sectionRef: 'to_do' })
            }
            canCreateTask={canCreateTask}
          />

          <TaskBoardToolbar
            search={search}
            onSearch={setSearch}
            users={assignmentUsers}
            selectedAssigneeIds={selectedAssigneeIds}
            onToggleAssignee={(id) =>
              setSelectedAssigneeIds((prev) => toggleArrayValue(prev, id))
            }
            selectedPriorities={selectedPriorities}
            onTogglePriority={(priority) =>
              setSelectedPriorities((prev) => toggleArrayValue(prev, priority))
            }
            tags={availableTags.length ? availableTags : TASK_TAG_OPTIONS}
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
            sortMode={sortMode}
            onSortModeChange={setSortMode}
            density={density}
            onDensityChange={setDensity}
          />

          {isInitialLoading ? (
            <TaskBoardSkeleton />
          ) : !organisationBoard ? (
            <Alert severity="info">No task board is available.</Alert>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flex-start',
                  overflowX: 'auto',
                  pb: 2,
                }}
              >
                {displayColumns.map((section) => (
                  <TaskColumn
                    key={section.ref}
                    section={section}
                    usersById={usersById}
                    density={density}
                    pendingMoveTaskIds={pendingMoveTaskIds}
                    onAddTask={() =>
                      setCreateDialog({
                        open: true,
                        sectionRef: section.ref,
                      })
                    }
                    onOpenTask={handleOpenTask}
                  />
                ))}
              </Box>
            </DragDropContext>
          )}
        </Stack>

        <TaskEditorDialog
          mode="create"
          open={createDialog.open}
          task={null}
          defaultSectionRef={createDialog.sectionRef}
          currentUserId={currentDashboardUserId}
          sections={displayColumns}
          users={assignmentUsers}
          usersById={usersById}
          tagOptions={availableTags}
          canAssignTasks={canAssignTasks}
          canEditAllComments={canEditAllTaskComments}
          isSubmitting={
            createTaskMutation.isLoading ||
            uploadTaskAttachmentsMutation.isLoading
          }
          isUploading={uploadTaskAttachmentsMutation.isLoading}
          isDeletingAttachment={deleteTaskAttachmentMutation.isLoading}
          isArchiving={false}
          isCommentSubmitting={false}
          isCommentEditing={false}
          commentError={null}
          commentEditError={null}
          onClose={() => setCreateDialog({ open: false })}
          onCreate={handleCreateTask}
          onSave={handleSaveTask}
          onArchive={(task) => archiveTaskMutation.mutate(task.id)}
          onUploadAttachments={handleUploadTaskAttachments}
          onDeleteAttachment={handleDeleteTaskAttachment}
          onAddComment={handleAddTaskComment}
          onUpdateComment={handleUpdateTaskComment}
        />

        <TaskEditorDialog
          mode="edit"
          open={Boolean(selectedTaskId)}
          task={selectedTask}
          currentUserId={currentDashboardUserId}
          sections={displayColumns}
          users={assignmentUsers}
          usersById={usersById}
          tagOptions={availableTags}
          canAssignTasks={canAssignTasks}
          canEditAllComments={canEditAllTaskComments}
          isSubmitting={
            updateTaskMutation.isLoading ||
            uploadTaskAttachmentsMutation.isLoading
          }
          isUploading={uploadTaskAttachmentsMutation.isLoading}
          isDeletingAttachment={deleteTaskAttachmentMutation.isLoading}
          isArchiving={archiveTaskMutation.isLoading}
          isCommentSubmitting={addTaskCommentMutation.isLoading}
          isCommentEditing={updateTaskCommentMutation.isLoading}
          commentError={addTaskCommentMutation.error}
          commentEditError={updateTaskCommentMutation.error}
          onClose={handleCloseTask}
          onCreate={handleCreateTask}
          onSave={handleSaveTask}
          onArchive={(task) => archiveTaskMutation.mutate(task.id)}
          onUploadAttachments={handleUploadTaskAttachments}
          onDeleteAttachment={handleDeleteTaskAttachment}
          onAddComment={handleAddTaskComment}
          onUpdateComment={handleUpdateTaskComment}
        />
      </Container>
    </Page>
  );
}

type TaskPageHeaderProps = {
  totalTasks: number;
  visibleTasks: number;
  urgentTasks: number;
  canCreateTask: boolean;
  onCreateTask: VoidFunction;
};

function TaskPageHeader({
  totalTasks,
  visibleTasks,
  urgentTasks,
  canCreateTask,
  onCreateTask,
}: TaskPageHeaderProps) {
  const taskLabel = totalTasks === 1 ? 'task' : 'tasks';

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      alignItems={{ xs: 'stretch', sm: 'flex-start' }}
      justifyContent="space-between"
    >
      <Box>
        <Typography variant="h3" sx={{ color: '#0f172a', lineHeight: 1.1 }}>
          Tasks
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
          Dashboard · Tasks · {visibleTasks} of {totalTasks} {taskLabel} ·{' '}
          {urgentTasks} urgent
        </Typography>
      </Box>

      <Button
        variant="contained"
        startIcon={<Iconify icon="eva:plus-fill" />}
        onClick={onCreateTask}
        disabled={!canCreateTask}
        sx={{
          alignSelf: { xs: 'stretch', sm: 'flex-start' },
          bgcolor: '#16a34a',
          borderRadius: 1.25,
          boxShadow: '0 8px 18px rgba(22, 163, 74, 0.22)',
          fontWeight: 700,
          px: 2.5,
          py: 1.1,
          '&:hover': { bgcolor: '#15803d' },
        }}
      >
        New task
      </Button>
    </Stack>
  );
}

type TaskBoardToolbarProps = {
  search: string;
  onSearch: (value: string) => void;
  users: TaskDashboardUser[];
  selectedAssigneeIds: number[];
  onToggleAssignee: (id: number) => void;
  selectedPriorities: TaskPriority[];
  onTogglePriority: (priority: TaskPriority) => void;
  tags: TaskTagOption[];
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  sortMode: TaskSortMode;
  onSortModeChange: (value: TaskSortMode) => void;
  density: TaskDensity;
  onDensityChange: (value: TaskDensity) => void;
};

function TaskBoardToolbar({
  search,
  onSearch,
  users,
  selectedAssigneeIds,
  onToggleAssignee,
  selectedPriorities,
  onTogglePriority,
  tags,
  selectedTags,
  onTagsChange,
  sortMode,
  onSortModeChange,
  density,
  onDensityChange,
}: TaskBoardToolbarProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.25, md: 1.5 },
        borderRadius: 1.5,
        borderColor: '#e2e8f0',
        bgcolor: 'common.white',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gap: 1.25,
          alignItems: 'center',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(0, 1fr) auto',
            xl: 'minmax(360px, 0.85fr) minmax(520px, 1.35fr) auto',
          },
          gridTemplateAreas: {
            xs: '"search" "filters" "actions"',
            md: '"search actions" "filters filters"',
            xl: '"search filters actions"',
          },
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{
            gridArea: 'search',
            minWidth: 0,
          }}
        >
          <TextField
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder="Search tasks..."
            size="small"
            sx={{
              flex: 1,
              minWidth: { sm: 240 },
              maxWidth: { xl: 360 },
              '& .MuiOutlinedInput-root': {
                bgcolor: '#f8fafc',
                borderRadius: 1,
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: '#94a3b8' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" width={16} height={16} />
                </InputAdornment>
              ),
            }}
          />

          {!!users.length && (
            <Stack
              direction="row"
              sx={{
                minHeight: 32,
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', sm: 'center' },
                pl: { sm: 0.25 },
              }}
            >
              {users.map((user, index) => {
                const selected = selectedAssigneeIds.includes(user.id);
                return (
                  <Tooltip key={user.id} title={getTaskUserDisplayName(user)}>
                    <Avatar
                      alt={getTaskUserDisplayName(user)}
                      src={getAvatarSrc(user)}
                      onClick={() => onToggleAssignee(user.id)}
                      sx={{
                        width: 32,
                        height: 32,
                        cursor: 'pointer',
                        fontSize: 11,
                        fontWeight: 800,
                        color: '#ffffff',
                        bgcolor: getAvatarColor(user.id),
                        ml: index === 0 ? 0 : '-6px',
                        position: 'relative',
                        zIndex: selected
                          ? users.length + 1
                          : users.length - index,
                        border: selected
                          ? '3px solid #0f172a'
                          : '3px solid #ffffff',
                        boxShadow: selected
                          ? '0 0 0 2px rgba(15, 23, 42, 0.16)'
                          : 'none',
                        '&:hover': {
                          zIndex: users.length + 2,
                        },
                      }}
                    >
                      {getInitials(user)}
                    </Avatar>
                  </Tooltip>
                );
              })}
            </Stack>
          )}
        </Stack>

        <Box
          sx={{
            gridArea: 'filters',
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.75,
              alignItems: 'center',
            }}
          >
            {(['URGENT', 'HIGH', 'NORMAL', 'LOW'] as TaskPriority[]).map(
              (priority) => (
                <FilterChip
                  key={priority}
                  label={PRIORITY_META[priority].label}
                  selected={selectedPriorities.includes(priority)}
                  onClick={() => onTogglePriority(priority)}
                />
              )
            )}

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: { xs: 'none', sm: 'block' },
                height: 24,
                my: 0.35,
                mx: 0.25,
                borderColor: '#e2e8f0',
              }}
            />

            <TextField
              select
              size="small"
              value={selectedTags}
              onChange={(event) => {
                const { value } = event.target;
                onTagsChange(
                  typeof value === 'string' ? value.split(',') : value
                );
              }}
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: (selected) => {
                  const selectedValues = selected as string[];
                  if (!selectedValues.length) return 'Tags filter';

                  return `Tags (${selectedValues.length})`;
                },
                inputProps: { 'aria-label': 'Filter tags' },
              }}
              sx={{
                minWidth: 180,
                '& .MuiOutlinedInput-root': {
                  height: 34,
                  borderRadius: 999,
                  bgcolor: '#ffffff',
                  fontWeight: 800,
                  color: selectedTags.length ? '#0f172a' : '#64748b',
                },
              }}
            >
              {tags.map((tag) => (
                <MenuItem key={tag.value} value={tag.value}>
                  <Checkbox
                    size="small"
                    checked={selectedTags.includes(tag.value)}
                    sx={{
                      p: 0.5,
                      mr: 0.75,
                      color: '#cbd5e1',
                      '&.Mui-checked': { color: tag.color },
                    }}
                  />
                  <Box
                    component="span"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: tag.color,
                      mr: 1,
                      flexShrink: 0,
                    }}
                  />
                  <ListItemText
                    primary={tag.label}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 800,
                      color: '#475569',
                    }}
                  />
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="flex-end"
          sx={{ gridArea: 'actions' }}
        >
          <TextField
            select
            size="small"
            value={sortMode}
            onChange={(event) =>
              onSortModeChange(event.target.value as TaskSortMode)
            }
            SelectProps={{
              inputProps: { 'aria-label': 'Sort tasks' },
            }}
            sx={{
              minWidth: { sm: 170 },
              '& .MuiOutlinedInput-root': { borderRadius: 1 },
            }}
          >
            {SORT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                Sort: {option.label}
              </MenuItem>
            ))}
          </TextField>

          <Stack
            direction="row"
            sx={{
              bgcolor: '#f1f5f9',
              borderRadius: 1,
              p: 0.4,
            }}
          >
            {(['comfortable', 'compact'] as TaskDensity[]).map((value) => (
              <Button
                key={value}
                size="small"
                color="inherit"
                onClick={() => onDensityChange(value)}
                sx={{
                  px: 1.5,
                  flex: { xs: 1, sm: '0 0 auto' },
                  minWidth: 0,
                  borderRadius: 0.75,
                  color: density === value ? '#0f172a' : '#64748b',
                  bgcolor: density === value ? '#ffffff' : 'transparent',
                  boxShadow:
                    density === value ? '0 1px 4px rgba(15, 23, 42, 0.08)' : 0,
                  '&:hover': {
                    bgcolor: density === value ? '#ffffff' : '#e2e8f0',
                  },
                }}
              >
                {value === 'comfortable' ? 'Comfortable' : 'Compact'}
              </Button>
            ))}
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
}

type FilterChipProps = {
  label: string;
  selected: boolean;
  dotColor?: string;
  onClick: VoidFunction;
};

function FilterChip({ label, selected, dotColor, onClick }: FilterChipProps) {
  return (
    <Chip
      clickable
      label={label}
      onClick={onClick}
      variant="outlined"
      icon={
        dotColor ? (
          <Box
            component="span"
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: dotColor,
              ml: 0.5,
            }}
          />
        ) : undefined
      }
      sx={{
        height: 30,
        px: 0.2,
        borderRadius: 999,
        fontWeight: 700,
        fontSize: 13,
        color: selected ? '#0f172a' : '#475569',
        bgcolor: selected ? '#f8fafc' : 'common.white',
        borderColor: selected ? '#94a3b8' : '#e2e8f0',
        '& .MuiChip-icon': { ml: 1 },
      }}
    />
  );
}

type TaskColumnProps = {
  section: ReturnType<typeof toTaskBoardColumns>[number];
  usersById: Record<number, TaskDashboardUser>;
  density: TaskDensity;
  pendingMoveTaskIds: number[];
  onAddTask: VoidFunction;
  onOpenTask: (task: Task) => void;
};

function TaskColumn({
  section,
  usersById,
  density,
  pendingMoveTaskIds,
  onAddTask,
  onOpenTask,
}: TaskColumnProps) {
  const accent = SECTION_ACCENTS[section.ref] ?? SECTION_ACCENTS.to_do;

  return (
    <Paper
      variant="outlined"
      sx={{
        width: 304,
        flex: '0 0 304px',
        bgcolor: accent.bg,
        borderColor: accent.border,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.65 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 9,
              height: 9,
              borderRadius: '50%',
              bgcolor: accent.dot,
            }}
          />
          <Typography variant="subtitle1" sx={{ color: '#0f172a' }}>
            {section.name}
          </Typography>
          <Chip
            size="small"
            label={section.count}
            sx={{
              height: 22,
              fontWeight: 800,
              color: '#64748b',
              bgcolor: '#e2e8f0',
            }}
          />
        </Stack>
        <Tooltip title={`Add task to ${section.name}`}>
          <IconButton size="small" onClick={onAddTask}>
            <Iconify icon="eva:plus-fill" width={20} height={20} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Droppable droppableId={section.ref} type="task">
        {(provided, snapshot) => (
          <Stack
            ref={provided.innerRef}
            {...provided.droppableProps}
            spacing={1.5}
            sx={{
              minHeight: 260,
              p: 1.5,
              pt: 0.25,
              bgcolor: snapshot.isDraggingOver
                ? alpha(accent.dot, 0.08)
                : 'transparent',
            }}
          >
            {section.tasks.map((task, index) => (
              <TaskCard
                key={task.id}
                task={task}
                index={index}
                assignees={getTaskAssigneeUsers(task, usersById)}
                density={density}
                disabled={pendingMoveTaskIds.includes(task.id)}
                onOpen={() => onOpenTask(task)}
              />
            ))}

            {provided.placeholder}

            <Button
              fullWidth
              color="inherit"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={onAddTask}
              sx={{
                border: '1px dashed #cbd5e1',
                borderRadius: 1.25,
                color: '#64748b',
                bgcolor: alpha('#ffffff', 0.45),
                py: 1.25,
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#94a3b8',
                  bgcolor: alpha('#ffffff', 0.7),
                },
              }}
            >
              Add task
            </Button>
          </Stack>
        )}
      </Droppable>
    </Paper>
  );
}

type TaskCardProps = {
  task: Task;
  index: number;
  assignees: TaskDashboardUser[];
  density: TaskDensity;
  disabled: boolean;
  onOpen: VoidFunction;
};

function TaskCard({
  task,
  index,
  assignees,
  density,
  disabled,
  onOpen,
}: TaskCardProps) {
  const due = getDuePresentation(task);
  const tags = getTaskTags(task);
  const isComplete = Boolean(task.completed_at);
  const compact = density === 'compact';
  const assigneeNames = assignees.map(getTaskUserDisplayName);

  return (
    <Draggable
      draggableId={String(task.id)}
      index={index}
      isDragDisabled={disabled}
    >
      {(provided, snapshot) => (
        <Paper
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          variant="outlined"
          onClick={onOpen}
          sx={{
            p: compact ? 1.5 : 2,
            cursor: 'pointer',
            borderRadius: 1.5,
            bgcolor: 'common.white',
            borderColor: '#e2e8f0',
            boxShadow: snapshot.isDragging
              ? '0 18px 40px rgba(15, 23, 42, 0.18)'
              : '0 2px 8px rgba(15, 23, 42, 0.06)',
            opacity: isComplete ? 0.72 : 1,
            '&:hover': {
              boxShadow: '0 12px 28px rgba(15, 23, 42, 0.12)',
            },
          }}
        >
          <Stack spacing={compact ? 1 : 1.35}>
            <Typography
              variant="caption"
              sx={{ color: '#94a3b8', fontWeight: 800 }}
            >
              {getTaskDisplayReference(task.id)}
            </Typography>

            <Typography
              variant="subtitle2"
              sx={{
                color: '#0f172a',
                fontSize: compact ? 14 : 16,
                lineHeight: 1.28,
                wordBreak: 'break-word',
                textDecoration: isComplete ? 'line-through' : 'none',
              }}
            >
              {task.title}
            </Typography>

            {!!tags.length && !compact && (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {tags.map((tag) => (
                  <TaskTagChip key={tag} tag={tag} />
                ))}
              </Stack>
            )}

            <Stack direction="row" alignItems="center" spacing={1}>
              <TaskPriorityPill priority={task.priority} />
              <Typography
                variant="caption"
                sx={{
                  color:
                    due.tone === 'overdue'
                      ? '#dc2626'
                      : due.tone === 'done'
                      ? '#16a34a'
                      : '#64748b',
                  fontWeight:
                    due.tone === 'overdue' || due.tone === 'today' ? 800 : 600,
                }}
              >
                {due.label}
              </Typography>

              <Box sx={{ flexGrow: 1 }} />

              {assignees.length ? (
                <Tooltip title={assigneeNames.join(', ')}>
                  <Stack direction="row" sx={{ flexShrink: 0 }}>
                    {assignees.slice(0, 3).map((assignee, assigneeIndex) => (
                      <Avatar
                        key={assignee.id}
                        alt={getTaskUserDisplayName(assignee)}
                        src={getAvatarSrc(assignee)}
                        sx={{
                          width: 28,
                          height: 28,
                          ml: assigneeIndex === 0 ? 0 : '-7px',
                          fontSize: 11,
                          fontWeight: 800,
                          color: '#ffffff',
                          bgcolor: getAvatarColor(assignee.id),
                          border: '2px solid #ffffff',
                        }}
                      >
                        {getInitials(assignee)}
                      </Avatar>
                    ))}
                    {assignees.length > 3 && (
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          ml: '-7px',
                          fontSize: 11,
                          fontWeight: 800,
                          color: '#475569',
                          bgcolor: '#e2e8f0',
                          border: '2px solid #ffffff',
                        }}
                      >
                        +{assignees.length - 3}
                      </Avatar>
                    )}
                  </Stack>
                </Tooltip>
              ) : (
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: 13,
                    fontWeight: 800,
                    bgcolor: '#e5e7eb',
                    color: '#94a3b8',
                  }}
                >
                  -
                </Avatar>
              )}
            </Stack>
          </Stack>
        </Paper>
      )}
    </Draggable>
  );
}

function TaskPriorityPill({ priority }: { priority: TaskPriority }) {
  const meta = PRIORITY_META[priority];

  return (
    <Box
      component="span"
      sx={{
        px: 0.9,
        py: 0.25,
        borderRadius: 0.75,
        bgcolor: meta.bg,
        color: meta.color,
        fontSize: 12,
        fontWeight: 900,
        lineHeight: 1.4,
        textTransform: 'uppercase',
      }}
    >
      {meta.label}
    </Box>
  );
}

type TaskTagChipProps = Omit<
  ComponentProps<typeof Chip>,
  'icon' | 'label' | 'onClick'
> & {
  tag: string;
  selected?: boolean;
  onClick?: VoidFunction;
};

function TaskTagChip({
  tag,
  selected = true,
  onClick,
  ...chipProps
}: TaskTagChipProps) {
  const meta = getTagMeta(tag);
  const clickable = Boolean(onClick);

  return (
    <Chip
      {...chipProps}
      size="small"
      label={meta.label}
      clickable={clickable}
      onClick={onClick}
      icon={
        <Box
          component="span"
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: meta.color,
            ml: 0.5,
          }}
        />
      }
      sx={{
        height: 24,
        borderRadius: 0.75,
        cursor: clickable ? 'pointer' : 'default',
        color: selected ? meta.color : '#64748b',
        bgcolor: selected ? meta.bg : '#ffffff',
        border: selected
          ? `1px solid ${alpha(meta.color, 0.28)}`
          : '1px solid #e2e8f0',
        fontWeight: 700,
        '& .MuiChip-icon': { ml: 0.8 },
      }}
    />
  );
}

type TaskEditorDialogProps = {
  mode: 'create' | 'edit';
  open: boolean;
  task: Task | null;
  defaultSectionRef?: string;
  currentUserId?: number | null;
  sections: ReturnType<typeof toTaskBoardColumns>;
  users: TaskDashboardUser[];
  usersById: Record<number, TaskDashboardUser>;
  tagOptions: TaskTagOption[];
  canAssignTasks: boolean;
  canEditAllComments: boolean;
  isSubmitting: boolean;
  isUploading: boolean;
  isDeletingAttachment: boolean;
  isArchiving: boolean;
  isCommentSubmitting: boolean;
  isCommentEditing: boolean;
  commentError: unknown;
  commentEditError: unknown;
  onClose: VoidFunction;
  onCreate: (payload: CreateTaskDto, attachmentFiles?: File[]) => Promise<void>;
  onSave: (
    task: Task,
    detailsPayload: UpdateTaskDto,
    movePayload?: MoveTaskDto
  ) => Promise<void>;
  onArchive: (task: Task) => void;
  onUploadAttachments: (task: Task, files: File[]) => Promise<void>;
  onDeleteAttachment: (
    task: Task,
    attachmentId: string | number
  ) => Promise<void>;
  onAddComment: (task: Task, comment: string) => Promise<void>;
  onUpdateComment: (
    task: Task,
    comment: TaskComment,
    nextComment: string
  ) => Promise<void>;
};

function TaskEditorDialog({
  mode,
  open,
  task,
  defaultSectionRef,
  currentUserId,
  sections,
  users,
  usersById,
  tagOptions,
  canAssignTasks,
  canEditAllComments,
  isSubmitting,
  isUploading,
  isDeletingAttachment,
  isArchiving,
  isCommentSubmitting,
  isCommentEditing,
  commentError,
  commentEditError,
  onClose,
  onCreate,
  onSave,
  onArchive,
  onUploadAttachments,
  onDeleteAttachment,
  onAddComment,
  onUpdateComment,
}: TaskEditorDialogProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('NORMAL');
  const [dueAt, setDueAt] = useState('');
  const [sectionRef, setSectionRef] = useState('');
  const [assigneeIds, setAssigneeIds] = useState<number[]>([]);
  const [selectedTaskTags, setSelectedTaskTags] = useState<string[]>([]);
  const [tagInputValue, setTagInputValue] = useState('');
  const [tagError, setTagError] = useState('');
  const [commentText, setCommentText] = useState('');
  const [commentCaretIndex, setCommentCaretIndex] = useState(0);
  const [hasSubmittedComment, setHasSubmittedComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [hasSubmittedCommentEdit, setHasSubmittedCommentEdit] = useState(false);
  const [activeTaskDialogTab, setActiveTaskDialogTab] =
    useState<TaskEditorDialogTab>('comments');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [pendingAttachmentFiles, setPendingAttachmentFiles] = useState<File[]>(
    []
  );
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSubmittingLocally, setIsSubmittingLocally] = useState(false);
  const formInitialisationKey = useRef<string | null>(null);
  const commentInputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(
    null
  );
  const initialMoveValues = useRef<{
    assigneeIds: number[];
    sectionRef: string;
  } | null>(null);
  const submitting = isSubmitting || isSubmittingLocally;

  useEffect(() => {
    if (!open) {
      formInitialisationKey.current = null;
      initialMoveValues.current = null;
      setIsSubmittingLocally(false);
      setCommentText('');
      setCommentCaretIndex(0);
      setHasSubmittedComment(false);
      setEditingCommentId(null);
      setEditingCommentText('');
      setHasSubmittedCommentEdit(false);
      setActiveTaskDialogTab('comments');
      setIsDeleteConfirmOpen(false);
      setTagInputValue('');
      return;
    }

    const formKey = task
      ? `edit:${task.id}`
      : `create:${defaultSectionRef ?? ''}`;

    if (formInitialisationKey.current === formKey) return;
    formInitialisationKey.current = formKey;
    setCommentText('');
    setCommentCaretIndex(0);
    setHasSubmittedComment(false);
    setEditingCommentId(null);
    setEditingCommentText('');
    setHasSubmittedCommentEdit(false);
    setActiveTaskDialogTab('comments');
    setIsDeleteConfirmOpen(false);
    setTagInputValue('');
    setTagError('');

    if (task) {
      const currentSection =
        sections.find((section) =>
          section.tasks.some((sectionTask) => sectionTask.id === task.id)
        ) ?? getColumnByRef(sections, String(task.section_id));
      const nextSectionRef = currentSection?.ref ?? sections[0]?.ref ?? 'to_do';
      const nextAssigneeIds = getTaskAssigneeIds(task);

      setTitle(task.title);
      setDescription(task.description ?? '');
      setPriority(task.priority);
      setDueAt(isoToDateInput(task.due_at));
      setSectionRef(nextSectionRef);
      setSelectedTaskTags(getTaskTags(task));
      setAssigneeIds(nextAssigneeIds);
      initialMoveValues.current = {
        assigneeIds: nextAssigneeIds,
        sectionRef: nextSectionRef,
      };
      setPendingAttachmentFiles([]);
    } else {
      setTitle('');
      setDescription('');
      setPriority('NORMAL');
      setDueAt('');
      setSectionRef(defaultSectionRef ?? sections[0]?.ref ?? 'to_do');
      setSelectedTaskTags([]);
      setAssigneeIds([]);
      initialMoveValues.current = null;
      setPendingAttachmentFiles([]);
    }

    setHasSubmitted(false);
  }, [defaultSectionRef, open, sections, task]);

  const handleSubmit = async () => {
    if (submitting) return;

    setHasSubmitted(true);
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;

    const pendingTag = normalizeTagText(tagInputValue);
    const nextTaskTags = pendingTag
      ? [...selectedTaskTags, pendingTag]
      : selectedTaskTags;
    const normalizedTags = normalizeTaskTags(nextTaskTags);
    const nextTagError = getInvalidTaskTagMessage(nextTaskTags);
    setSelectedTaskTags(normalizedTags);
    setTagInputValue('');
    setTagError(nextTagError);
    if (nextTagError) return;

    setIsSubmittingLocally(true);

    try {
      if (!task) {
        const nextAssigneeIds = normalizeTaskAssigneeIds(assigneeIds);

        await onCreate(
          {
            title: trimmedTitle,
            description: description.trim() || null,
            priority,
            due_at: dateInputToIso(dueAt),
            tags: normalizedTags,
            section_ref: sectionRef,
            assigned_dashboard_user_ids: nextAssigneeIds,
          },
          pendingAttachmentFiles
        );
        return;
      }

      const detailsPayload: UpdateTaskDto = {
        title: trimmedTitle,
        description: description.trim() || null,
        priority,
        due_at: dateInputToIso(dueAt),
        tags: normalizedTags,
      };
      const movePayload: MoveTaskDto = {};
      const nextAssigneeIds = normalizeTaskAssigneeIds(assigneeIds);
      const initialSectionRef =
        initialMoveValues.current?.sectionRef ?? sectionRef;
      const initialAssigneeIds =
        initialMoveValues.current?.assigneeIds ?? getTaskAssigneeIds(task);
      const assignmentChanged = !areTaskAssigneeIdsEqual(
        nextAssigneeIds,
        initialAssigneeIds
      );

      if (sectionRef && sectionRef !== initialSectionRef) {
        movePayload.section_ref = sectionRef;
      }

      if (canAssignTasks && assignmentChanged) {
        movePayload.section_ref = movePayload.section_ref ?? sectionRef;
      }

      if (hasMovePayload(movePayload)) {
        movePayload.assigned_dashboard_user_ids = nextAssigneeIds;
      }

      await onSave(
        task,
        detailsPayload,
        hasMovePayload(movePayload) ? movePayload : undefined
      );
    } finally {
      setIsSubmittingLocally(false);
    }
  };

  const handleAttachmentFiles = async (files: File[]) => {
    if (!files.length) return;

    if (task) {
      await onUploadAttachments(task, files);
      return;
    }

    setPendingAttachmentFiles((prev) => [...prev, ...files]);
  };

  const handleAttachmentInputChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    await handleAttachmentFiles(Array.from(event.target.files ?? []));
    event.target.value = '';
  };

  const handleAttachmentDrop = async (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    await handleAttachmentFiles(Array.from(event.dataTransfer.files ?? []));
  };

  const handleRemovePendingAttachment = (index: number) => {
    setPendingAttachmentFiles((prev) =>
      prev.filter((_file, fileIndex) => fileIndex !== index)
    );
  };

  const handleAssigneeUsersChange = (nextUsers: TaskDashboardUser[]) => {
    setAssigneeIds(normalizeTaskAssigneeIds(nextUsers.map((user) => user.id)));
  };

  const handleClearAssignees = () => {
    setAssigneeIds([]);
  };

  const handleTaskTagsChange = (nextTags: string[]) => {
    setTagError(getInvalidTaskTagMessage(nextTags));
    setSelectedTaskTags(normalizeTaskTags(nextTags));
  };

  const handleTagInputChange = (value: string) => {
    const pendingTag = normalizeTagText(value);
    const nextTaskTags = pendingTag
      ? [...selectedTaskTags, pendingTag]
      : selectedTaskTags;

    setTagInputValue(value);
    setTagError(getInvalidTaskTagMessage(nextTaskTags));
  };

  const handleDeletePersistedAttachment = async (
    attachmentId: string | number | undefined
  ) => {
    if (!task || attachmentId === undefined) return;

    await onDeleteAttachment(task, attachmentId);
  };

  const handleCommentTextChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const nextValue = event.target.value;

    setCommentText(nextValue);
    setCommentCaretIndex(event.target.selectionStart ?? nextValue.length);
  };

  const handleCommentTextSelect = (
    event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const target = event.currentTarget;

    setCommentCaretIndex(target.selectionStart ?? commentText.length);
  };

  const handleSubmitComment = async () => {
    if (!task || isCommentSubmitting) return;

    setHasSubmittedComment(true);
    const trimmedComment = commentText.trim();
    if (!trimmedComment) return;

    try {
      await onAddComment(task, trimmedComment);
      setCommentText('');
      setHasSubmittedComment(false);
    } catch {
      // The mutation error is rendered inline from commentError.
    }
  };

  const resetCommentEdit = useCallback(() => {
    setEditingCommentId(null);
    setEditingCommentText('');
    setHasSubmittedCommentEdit(false);
  }, []);

  const handleStartEditComment = (comment: TaskComment) => {
    if (isCommentEditing) return;

    setEditingCommentId(comment.id);
    setEditingCommentText(comment.comment);
    setHasSubmittedCommentEdit(false);
  };

  const handleCancelEditComment = () => {
    if (isCommentEditing) return;

    resetCommentEdit();
  };

  const handleEditedCommentTextChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setEditingCommentText(event.target.value);
  };

  const handleEditedCommentTextValueChange = (value: string) => {
    setEditingCommentText(value);
  };

  const handleSubmitCommentEdit = async (comment: TaskComment) => {
    if (!task || isCommentEditing) return;

    setHasSubmittedCommentEdit(true);
    const trimmedComment = editingCommentText.trim();
    if (!trimmedComment) return;

    if (trimmedComment === comment.comment.trim()) {
      resetCommentEdit();
      return;
    }

    try {
      await onUpdateComment(task, comment, trimmedComment);
      resetCommentEdit();
    } catch {
      // The mutation error is rendered inline from commentEditError.
    }
  };

  const handleConfirmDeleteTask = () => {
    if (!task || isArchiving) return;

    onArchive(task);
  };

  const attachments = task ? getTaskAttachments(task) : [];
  const attachmentFiles = task ? [] : pendingAttachmentFiles;
  const taskId = task?.id;
  const isEdit = mode === 'edit';
  const sortedComments = useMemo(
    () => sortTaskComments(task?.comments ?? []),
    [task?.comments]
  );
  const sortedActivity = useMemo(
    () => sortTaskActivity(task?.activity ?? []),
    [task?.activity]
  );
  const sectionsById = useMemo(
    () => buildTaskSectionNamesById(sections),
    [sections]
  );
  const selectedAssigneeUsers = useMemo(
    () =>
      assigneeIds.map((id) => usersById[id] ?? getFallbackDashboardUser(id)),
    [assigneeIds, usersById]
  );
  useEffect(() => {
    if (
      editingCommentId !== null &&
      !sortedComments.some((comment) => comment.id === editingCommentId)
    ) {
      resetCommentEdit();
    }
  }, [editingCommentId, resetCommentEdit, sortedComments]);
  const trimmedCommentText = commentText.trim();
  const trimmedEditingCommentText = editingCommentText.trim();
  const mentionUsers = useMemo(() => {
    const usersMap = new Map<number, TaskDashboardUser>();

    users.forEach((user) => usersMap.set(user.id, user));
    Object.values(usersById).forEach((user) => usersMap.set(user.id, user));

    return Array.from(usersMap.values()).sort((left, right) =>
      getTaskUserDisplayName(left).localeCompare(getTaskUserDisplayName(right))
    );
  }, [users, usersById]);
  const activeCommentMention = useMemo(
    () => getActiveCommentMention(commentText, commentCaretIndex),
    [commentCaretIndex, commentText]
  );
  const filteredMentionUsers = useMemo(
    () => filterMentionUsers(mentionUsers, activeCommentMention),
    [activeCommentMention, mentionUsers]
  );
  const showMentionSuggestions =
    Boolean(task) &&
    Boolean(activeCommentMention) &&
    filteredMentionUsers.length > 0 &&
    !isCommentSubmitting;
  const handleTaskDialogTabChange = (
    _event: SyntheticEvent,
    value: TaskEditorDialogTab
  ) => {
    setActiveTaskDialogTab(value);
  };
  const descriptionLinks = useMemo(
    () => getTaskDescriptionLinks(description),
    [description]
  );

  const handleSelectMentionUser = (user: TaskDashboardUser) => {
    if (!activeCommentMention) return;

    const mentionText = `@${getTaskUserDisplayName(user)}`;
    const nextCommentText = `${commentText.slice(
      0,
      activeCommentMention.start
    )}${mentionText} ${commentText.slice(activeCommentMention.end)}`;
    const nextCaretIndex = activeCommentMention.start + mentionText.length + 1;

    setCommentText(nextCommentText);
    setCommentCaretIndex(nextCaretIndex);

    setTimeout(() => {
      commentInputRef.current?.focus();
      commentInputRef.current?.setSelectionRange(
        nextCaretIndex,
        nextCaretIndex
      );
    }, 0);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            overflow: 'hidden',
          },
        }}
      >
        <DialogContent sx={{ p: { xs: 2.5, md: 4 } }}>
          {!task && isEdit ? (
            <Stack spacing={2}>
              <Skeleton height={44} />
              <Skeleton height={160} />
            </Stack>
          ) : (
            <Stack spacing={2.4}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography variant="h3" sx={{ color: '#0f172a' }}>
                  {isEdit ? 'Edit task' : 'New task'}
                </Typography>
                {task ? (
                  <Typography variant="h4" sx={{ color: '#94a3b8' }}>
                    {getTaskDisplayReference(task.id)}
                  </Typography>
                ) : null}
                <Box sx={{ flexGrow: 1 }} />
                <IconButton onClick={onClose} size="large">
                  <Iconify icon="eva:close-fill" width={30} height={30} />
                </IconButton>
              </Stack>

              <Stack spacing={1}>
                <FieldLabel htmlFor="task-title">TITLE</FieldLabel>
                <TextField
                  id="task-title"
                  value={title}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setTitle(event.target.value)
                  }
                  error={hasSubmitted && !title.trim()}
                  helperText={
                    hasSubmitted && !title.trim() ? 'Title is required' : ''
                  }
                  fullWidth
                  inputProps={{ 'aria-label': 'Title' }}
                  sx={fieldSx}
                />
              </Stack>

              <Stack spacing={1}>
                <FieldLabel htmlFor="task-description">DESCRIPTION</FieldLabel>
                <TextField
                  id="task-description"
                  value={description}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setDescription(event.target.value)
                  }
                  placeholder="Optional details..."
                  multiline
                  minRows={6}
                  fullWidth
                  inputProps={{ 'aria-label': 'Description' }}
                  sx={fieldSx}
                />
                {!!descriptionLinks.length && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {descriptionLinks.map((link) => (
                      <Link
                        key={`${link.href}-${link.start}`}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{
                          color: '#2563eb',
                          fontSize: 14,
                          fontWeight: 800,
                          maxWidth: 1,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {link.text}
                      </Link>
                    ))}
                  </Stack>
                )}
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Stack spacing={1} sx={{ flex: 1 }}>
                  <FieldLabel htmlFor="task-status">STATUS</FieldLabel>
                  <TextField
                    id="task-status"
                    select
                    value={sectionRef}
                    onChange={(event) => setSectionRef(event.target.value)}
                    fullWidth
                    inputProps={{ 'aria-label': 'Status' }}
                    sx={fieldSx}
                  >
                    {sections.map((section) => (
                      <MenuItem key={section.ref} value={section.ref}>
                        {section.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Stack spacing={1} sx={{ flex: 1 }}>
                  <FieldLabel htmlFor="task-priority">PRIORITY</FieldLabel>
                  <TextField
                    id="task-priority"
                    select
                    value={priority}
                    onChange={(event) =>
                      setPriority(event.target.value as TaskPriority)
                    }
                    fullWidth
                    inputProps={{ 'aria-label': 'Priority' }}
                    sx={fieldSx}
                  >
                    {TASK_PRIORITY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {PRIORITY_META[option.value].label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Stack spacing={1} sx={{ flex: 1 }}>
                  <FieldLabel htmlFor="task-assignees">ASSIGNEES</FieldLabel>
                  <Autocomplete
                    multiple
                    openOnFocus
                    disablePortal
                    id="task-assignees"
                    options={users}
                    value={selectedAssigneeUsers}
                    onChange={(_event, nextUsers) =>
                      handleAssigneeUsersChange(nextUsers)
                    }
                    disabled={!canAssignTasks && isEdit}
                    getOptionLabel={(option) =>
                      getAssigneeOptionLabel(option, currentUserId)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    filterSelectedOptions
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          'aria-label': 'Assignees',
                        }}
                        placeholder={
                          selectedAssigneeUsers.length ? '' : 'Unassigned'
                        }
                        sx={fieldSx}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={getTaskUserDisplayName(option)}
                          avatar={
                            <Avatar
                              alt={getTaskUserDisplayName(option)}
                              src={getAvatarSrc(option)}
                            >
                              {getInitials(option)}
                            </Avatar>
                          }
                          sx={{ borderRadius: 0.85, fontWeight: 700 }}
                        />
                      ))
                    }
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        <Avatar
                          alt={getTaskUserDisplayName(option)}
                          src={getAvatarSrc(option)}
                          sx={{
                            width: 28,
                            height: 28,
                            mr: 1,
                            fontSize: 11,
                            fontWeight: 800,
                            color: '#ffffff',
                            bgcolor: getAvatarColor(option.id),
                          }}
                        >
                          {getInitials(option)}
                        </Avatar>
                        {getAssigneeOptionLabel(option, currentUserId)}
                      </Box>
                    )}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        ...fieldSx['& .MuiOutlinedInput-root'],
                        alignItems: 'flex-start',
                      },
                    }}
                  />
                  {!!selectedAssigneeUsers.length && (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleClearAssignees}
                      sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
                    >
                      Clear assignees
                    </Button>
                  )}
                </Stack>
                <Stack spacing={1} sx={{ flex: 1 }}>
                  <FieldLabel htmlFor="task-due-date">DUE DATE</FieldLabel>
                  <TextField
                    id="task-due-date"
                    type="date"
                    value={dueAt}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setDueAt(event.target.value)
                    }
                    fullWidth
                    inputProps={{ 'aria-label': 'Due date' }}
                    sx={fieldSx}
                  />
                </Stack>
              </Stack>

              <Stack spacing={1}>
                <FieldLabel htmlFor="task-tags">TAGS</FieldLabel>
                <Autocomplete
                  multiple
                  freeSolo
                  openOnFocus
                  disablePortal
                  id="task-tags"
                  options={tagOptions.map((option) => option.value)}
                  value={selectedTaskTags}
                  inputValue={tagInputValue}
                  onChange={(_event, nextTags) =>
                    handleTaskTagsChange(nextTags)
                  }
                  onInputChange={(_event, nextValue) =>
                    handleTagInputChange(nextValue)
                  }
                  getOptionLabel={(option) => getTagMeta(option).label}
                  filterSelectedOptions
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={Boolean(tagError)}
                      helperText={tagError}
                      inputProps={{
                        ...params.inputProps,
                        'aria-label': 'Tags',
                        maxLength: TASK_TAG_MAX_LENGTH,
                      }}
                      sx={fieldSx}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((tag, index) => (
                      <TaskTagChip
                        {...getTagProps({ index })}
                        key={`${tag}-${index}`}
                        tag={tag}
                      />
                    ))
                  }
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option}>
                      <TaskTagChip tag={option} />
                    </Box>
                  )}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      ...fieldSx['& .MuiOutlinedInput-root'],
                      alignItems: 'center',
                      py: 1.25,
                    },
                    '& .MuiAutocomplete-input': {
                      alignSelf: 'center',
                      lineHeight: '24px',
                      py: '0 !important',
                    },
                    '& .MuiAutocomplete-endAdornment': {
                      top: '50%',
                      transform: 'translateY(-50%)',
                    },
                  }}
                />
                {!tagError && (
                  <Typography
                    variant="caption"
                    sx={{ color: '#94a3b8', fontWeight: 600 }}
                  >
                    Press Enter to add new tags.
                  </Typography>
                )}
              </Stack>

              <Stack spacing={1}>
                <FieldLabel>ATTACHMENTS</FieldLabel>
                {(!!attachments.length || !!attachmentFiles.length) && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {taskId !== undefined &&
                      attachments.map((attachment, index) => (
                        <TaskAttachmentItem
                          key={attachment.id ?? `${attachment.name}-${index}`}
                          taskId={taskId}
                          attachment={attachment}
                          isDeleting={isDeletingAttachment}
                          onDelete={() =>
                            handleDeletePersistedAttachment(attachment.id)
                          }
                        />
                      ))}
                    {attachmentFiles.map((file, index) => (
                      <Chip
                        key={`${file.name}-${file.lastModified}-${index}`}
                        label={file.name}
                        onDelete={() => handleRemovePendingAttachment(index)}
                      />
                    ))}
                  </Stack>
                )}
                <Box
                  component="label"
                  onDragOver={(event: DragEvent<HTMLLabelElement>) =>
                    event.preventDefault()
                  }
                  onDrop={handleAttachmentDrop}
                  sx={{
                    border: '1px dashed #cbd5e1',
                    borderRadius: 1.5,
                    py: 2,
                    textAlign: 'center',
                    color: '#64748b',
                    cursor: isUploading ? 'default' : 'pointer',
                    fontWeight: 700,
                    display: 'block',
                    opacity: isUploading ? 0.72 : 1,
                    '&:hover': {
                      borderColor: isUploading ? '#cbd5e1' : '#94a3b8',
                      bgcolor: isUploading ? 'transparent' : '#f8fafc',
                    },
                  }}
                >
                  <input
                    hidden
                    multiple
                    type="file"
                    disabled={isUploading}
                    onChange={handleAttachmentInputChange}
                  />
                  <Iconify
                    icon="eva:plus-circle-outline"
                    width={18}
                    height={18}
                    sx={{ mr: 0.75, verticalAlign: 'text-bottom' }}
                  />
                  {isUploading
                    ? 'Uploading attachments...'
                    : 'Attach files — or drop them here'}
                </Box>
              </Stack>

              {Boolean(task) && (
                <Stack spacing={1.5}>
                  <Tabs
                    value={activeTaskDialogTab}
                    onChange={handleTaskDialogTabChange}
                    aria-label="Task history tabs"
                    sx={{
                      minHeight: 40,
                      borderBottom: '1px solid #e2e8f0',
                      '& .MuiTabs-indicator': {
                        bgcolor: '#0f172a',
                        height: 2,
                      },
                      '& .MuiTab-root': {
                        minHeight: 40,
                        px: 0,
                        mr: 2.5,
                        color: '#64748b',
                        fontWeight: 800,
                        textTransform: 'none',
                      },
                      '& .Mui-selected': {
                        color: '#0f172a',
                      },
                    }}
                  >
                    <Tab
                      id="task-tab-comments"
                      value="comments"
                      label={`Comments (${sortedComments.length})`}
                      aria-controls="task-tabpanel-comments"
                    />
                    <Tab
                      id="task-tab-activity"
                      value="activity"
                      label={`Activity (${sortedActivity.length})`}
                      aria-controls="task-tabpanel-activity"
                    />
                  </Tabs>

                  {activeTaskDialogTab === 'activity' ? (
                    <Box
                      id="task-tabpanel-activity"
                      role="tabpanel"
                      aria-labelledby="task-tab-activity"
                    >
                      <TaskActivityList
                        activity={sortedActivity}
                        usersById={usersById}
                        sectionsById={sectionsById}
                        mentionUsers={mentionUsers}
                      />
                    </Box>
                  ) : (
                    <Stack
                      id="task-tabpanel-comments"
                      role="tabpanel"
                      aria-labelledby="task-tab-comments"
                      spacing={1.25}
                    >
                      {sortedComments.length ? (
                        <Stack spacing={1}>
                          {sortedComments.map((comment) => (
                            <TaskCommentItem
                              key={comment.id}
                              comment={comment}
                              usersById={usersById}
                              canEdit={
                                canEditAllComments ||
                                currentUserId ===
                                  comment.actor_dashboard_user_id
                              }
                              isEditing={editingCommentId === comment.id}
                              editText={editingCommentText}
                              editError={
                                hasSubmittedCommentEdit &&
                                editingCommentId === comment.id
                                  ? commentEditError
                                  : null
                              }
                              showRequiredError={
                                hasSubmittedCommentEdit &&
                                editingCommentId === comment.id &&
                                !trimmedEditingCommentText
                              }
                              isSaving={
                                isCommentEditing &&
                                editingCommentId === comment.id
                              }
                              onStartEdit={() =>
                                handleStartEditComment(comment)
                              }
                              onEditTextChange={handleEditedCommentTextChange}
                              onEditTextValueChange={
                                handleEditedCommentTextValueChange
                              }
                              onCancelEdit={handleCancelEditComment}
                              onSaveEdit={() =>
                                handleSubmitCommentEdit(comment)
                              }
                              mentionUsers={mentionUsers}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                          No comments yet.
                        </Typography>
                      )}

                      {Boolean(commentError) && (
                        <Alert severity="error">
                          {getErrorMessage(commentError) ||
                            'There was an error while adding the comment'}
                        </Alert>
                      )}

                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                      >
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <TextField
                            id="task-comment"
                            value={commentText}
                            onChange={handleCommentTextChange}
                            inputRef={commentInputRef}
                            placeholder="Write a comment..."
                            multiline
                            minRows={2}
                            fullWidth
                            disabled={isCommentSubmitting}
                            error={hasSubmittedComment && !trimmedCommentText}
                            helperText={
                              hasSubmittedComment && !trimmedCommentText
                                ? 'Comment is required'
                                : ''
                            }
                            inputProps={{
                              'aria-label': 'Comment',
                              onClick: handleCommentTextSelect,
                              onKeyUp: handleCommentTextSelect,
                              onSelect: handleCommentTextSelect,
                            }}
                            sx={{
                              ...fieldSx,
                              '& .MuiInputBase-input': {
                                fontSize: 15,
                              },
                            }}
                          />
                          {showMentionSuggestions && (
                            <TaskMentionSuggestions
                              users={filteredMentionUsers}
                              onSelect={handleSelectMentionUser}
                            />
                          )}
                        </Box>
                        <LoadingButton
                          variant="contained"
                          loading={isCommentSubmitting}
                          disabled={!trimmedCommentText || isCommentSubmitting}
                          onClick={handleSubmitComment}
                          sx={{
                            px: 2.4,
                            py: 1.1,
                            borderRadius: 1.25,
                            bgcolor: '#0f172a',
                            fontWeight: 800,
                            minWidth: { sm: 132 },
                            '&:hover': { bgcolor: '#1e293b' },
                          }}
                        >
                          Add comment
                        </LoadingButton>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              )}

              <Divider />

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                {Boolean(task) && (
                  <LoadingButton
                    color="error"
                    loading={isArchiving}
                    disabled={isArchiving}
                    onClick={() => setIsDeleteConfirmOpen(true)}
                    sx={{ mr: { sm: 'auto' }, justifyContent: 'flex-start' }}
                  >
                    Delete task
                  </LoadingButton>
                )}
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={onClose}
                  sx={{ px: 3, py: 1.2, borderRadius: 1.25 }}
                >
                  Cancel
                </Button>
                <LoadingButton
                  variant="contained"
                  loading={submitting}
                  onClick={handleSubmit}
                  sx={{
                    px: 4,
                    py: 1.2,
                    borderRadius: 1.25,
                    bgcolor: '#16a34a',
                    fontWeight: 800,
                    '&:hover': { bgcolor: '#15803d' },
                  }}
                >
                  {isEdit ? 'Save changes' : 'Create task'}
                </LoadingButton>
              </Stack>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(task) && isDeleteConfirmOpen}
        onClose={() => {
          if (!isArchiving) setIsDeleteConfirmOpen(false);
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ color: '#0f172a' }}>Delete task?</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="body2" sx={{ color: '#475569' }}>
            This will delete{' '}
            {task ? getTaskDisplayReference(task.id) : 'this task'}. You cannot
            undo this action.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            color="inherit"
            disabled={isArchiving}
            onClick={() => setIsDeleteConfirmOpen(false)}
          >
            Keep task
          </Button>
          <LoadingButton
            color="error"
            variant="contained"
            loading={isArchiving}
            onClick={handleConfirmDeleteTask}
          >
            Delete task
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

type TaskCommentItemProps = {
  comment: TaskComment;
  usersById: Record<number, TaskDashboardUser>;
  mentionUsers: TaskDashboardUser[];
  canEdit: boolean;
  isEditing: boolean;
  editText: string;
  editError: unknown;
  showRequiredError: boolean;
  isSaving: boolean;
  onStartEdit: VoidFunction;
  onEditTextChange: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onEditTextValueChange: (value: string) => void;
  onCancelEdit: VoidFunction;
  onSaveEdit: VoidFunction;
};

type TaskActivityListProps = {
  activity: TaskActivity[];
  usersById: Record<number, TaskDashboardUser>;
  sectionsById: Record<number, string>;
  mentionUsers: TaskDashboardUser[];
};

type TaskActivityItemProps = {
  activity: TaskActivity;
  usersById: Record<number, TaskDashboardUser>;
  sectionsById: Record<number, string>;
  mentionUsers: TaskDashboardUser[];
  isLast: boolean;
};

type TaskMentionSuggestionsProps = {
  users: TaskDashboardUser[];
  onSelect: (user: TaskDashboardUser) => void;
};

function TaskMentionSuggestions({
  users,
  onSelect,
}: TaskMentionSuggestionsProps) {
  return (
    <Paper
      variant="outlined"
      aria-label="User mention suggestions"
      sx={{
        mt: 0.75,
        p: 0.5,
        borderRadius: 1.25,
        borderColor: '#e2e8f0',
        bgcolor: '#ffffff',
        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
      }}
    >
      <Stack spacing={0.25}>
        {users.map((user) => {
          const userName = getTaskUserDisplayName(user);

          return (
            <Button
              key={user.id}
              color="inherit"
              fullWidth
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onSelect(user)}
              startIcon={
                <Avatar
                  alt={userName}
                  src={getAvatarSrc(user)}
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: 11,
                    fontWeight: 800,
                    color: '#ffffff',
                    bgcolor: getAvatarColor(user.id),
                  }}
                >
                  {getInitials(user)}
                </Avatar>
              }
              sx={{
                justifyContent: 'flex-start',
                borderRadius: 0.85,
                px: 1,
                py: 0.75,
                color: '#0f172a',
                textTransform: 'none',
                '&:hover': { bgcolor: '#f8fafc' },
              }}
            >
              <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 800, lineHeight: 1.25 }}
                >
                  {userName}
                </Typography>
                {user.email && (
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {user.email}
                  </Typography>
                )}
              </Box>
            </Button>
          );
        })}
      </Stack>
    </Paper>
  );
}

function TaskActivityList({
  activity,
  usersById,
  sectionsById,
  mentionUsers,
}: TaskActivityListProps) {
  if (!activity.length) {
    return (
      <Typography variant="body2" sx={{ color: '#94a3b8' }}>
        No activity yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={0}>
      {activity.map((activityItem, index) => (
        <TaskActivityItem
          key={activityItem.id}
          activity={activityItem}
          usersById={usersById}
          sectionsById={sectionsById}
          mentionUsers={mentionUsers}
          isLast={index === activity.length - 1}
        />
      ))}
    </Stack>
  );
}

function TaskActivityItem({
  activity,
  usersById,
  sectionsById,
  mentionUsers,
  isLast,
}: TaskActivityItemProps) {
  const authorName = getTaskActivityAuthorName(activity, usersById);
  const author = usersById[activity.actor_dashboard_user_id];
  const presentation = useMemo(
    () => getTaskActivityPresentation(activity, sectionsById, usersById),
    [activity, sectionsById, usersById]
  );
  const commentTextParts = useMemo(
    () => getCommentMentionParts(presentation.comment ?? '', mentionUsers),
    [mentionUsers, presentation.comment]
  );

  return (
    <Stack
      data-testid="task-activity-item"
      direction="row"
      spacing={1.25}
      sx={{ minWidth: 0 }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignSelf: 'stretch',
          width: 34,
          flexShrink: 0,
          pt: 0.2,
        }}
      >
        <Avatar
          alt={authorName}
          src={getAvatarSrc(author)}
          sx={{
            width: 34,
            height: 34,
            fontSize: 12,
            fontWeight: 800,
            color: '#ffffff',
            bgcolor: getAvatarColor(activity.actor_dashboard_user_id),
            zIndex: 1,
          }}
        >
          {getInitials(author ?? { display_name: authorName })}
        </Avatar>
        {!isLast && (
          <Box
            sx={{
              position: 'absolute',
              top: 38,
              bottom: 0,
              width: 2,
              bgcolor: '#e2e8f0',
            }}
          />
        )}
      </Box>

      <Box sx={{ minWidth: 0, flexGrow: 1, pb: isLast ? 0 : 1.75 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0.25, sm: 1 }}
          alignItems={{ xs: 'flex-start', sm: 'baseline' }}
        >
          <Typography variant="subtitle2" sx={{ color: '#0f172a' }}>
            {authorName}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            {formatTaskActivityCreatedAt(activity)}
          </Typography>
        </Stack>

        <Typography
          variant="body2"
          sx={{
            color: '#334155',
            fontWeight: 700,
            mt: 0.15,
            overflowWrap: 'anywhere',
          }}
        >
          {presentation.title}
        </Typography>

        {!!presentation.details.length && (
          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 0.75 }}
          >
            {presentation.details.map((detail) => (
              <Chip
                key={detail}
                label={detail}
                size="small"
                sx={{
                  height: 'auto',
                  minHeight: 24,
                  borderRadius: 0.85,
                  bgcolor: '#f1f5f9',
                  color: '#475569',
                  fontWeight: 700,
                  '& .MuiChip-label': {
                    py: 0.35,
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                  },
                }}
              />
            ))}
          </Stack>
        )}

        {presentation.comment && (
          <Box
            sx={{
              mt: 0.85,
              border: '1px solid #e2e8f0',
              borderRadius: 1.25,
              p: 1,
              bgcolor: '#f8fafc',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#334155',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
              }}
            >
              {commentTextParts.map((part, index) =>
                part.mention ? (
                  <Box
                    key={`${part.text}-${index}`}
                    component="span"
                    data-testid="task-activity-mention"
                    sx={{ color: '#2563eb', fontWeight: 800 }}
                  >
                    {part.text}
                  </Box>
                ) : (
                  part.text
                )
              )}
            </Typography>
          </Box>
        )}
      </Box>
    </Stack>
  );
}

function TaskCommentItem({
  comment,
  usersById,
  mentionUsers,
  canEdit,
  isEditing,
  editText,
  editError,
  showRequiredError,
  isSaving,
  onStartEdit,
  onEditTextChange,
  onEditTextValueChange,
  onCancelEdit,
  onSaveEdit,
}: TaskCommentItemProps) {
  const authorName = getTaskCommentAuthorName(comment, usersById);
  const author = usersById[comment.actor_dashboard_user_id];
  const trimmedEditText = editText.trim();
  const editInputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(
    null
  );
  const [editCaretIndex, setEditCaretIndex] = useState(0);
  const activeEditMention = useMemo(
    () => getActiveCommentMention(editText, editCaretIndex),
    [editCaretIndex, editText]
  );
  const commentTextParts = useMemo(
    () => getCommentMentionParts(comment.comment, mentionUsers),
    [comment.comment, mentionUsers]
  );
  const filteredEditMentionUsers = useMemo(
    () => filterMentionUsers(mentionUsers, activeEditMention),
    [activeEditMention, mentionUsers]
  );
  const showEditMentionSuggestions =
    Boolean(activeEditMention) &&
    filteredEditMentionUsers.length > 0 &&
    !isSaving;

  useEffect(() => {
    setEditCaretIndex(isEditing ? editText.length : 0);
  }, [comment.id, editText.length, isEditing]);

  const handleEditTextChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onEditTextChange(event);
    setEditCaretIndex(event.target.selectionStart ?? event.target.value.length);
  };

  const handleEditTextSelect = (
    event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const target = event.currentTarget;

    setEditCaretIndex(target.selectionStart ?? editText.length);
  };

  const handleSelectEditMentionUser = (user: TaskDashboardUser) => {
    if (!activeEditMention) return;

    const mentionText = `@${getTaskUserDisplayName(user)}`;
    const nextEditText = `${editText.slice(
      0,
      activeEditMention.start
    )}${mentionText} ${editText.slice(activeEditMention.end)}`;
    const nextCaretIndex = activeEditMention.start + mentionText.length + 1;

    onEditTextValueChange(nextEditText);
    setEditCaretIndex(nextCaretIndex);

    setTimeout(() => {
      editInputRef.current?.focus();
      editInputRef.current?.setSelectionRange(nextCaretIndex, nextCaretIndex);
    }, 0);
  };

  return (
    <Stack
      data-testid="task-comment-item"
      direction="row"
      spacing={1.25}
      sx={{
        border: '1px solid #e2e8f0',
        borderRadius: 1.25,
        p: 1.25,
        bgcolor: '#f8fafc',
      }}
    >
      <Avatar
        alt={authorName}
        src={getAvatarSrc(author)}
        sx={{
          width: 34,
          height: 34,
          fontSize: 12,
          fontWeight: 800,
          color: '#ffffff',
          bgcolor: getAvatarColor(comment.actor_dashboard_user_id),
          flexShrink: 0,
        }}
      >
        {getInitials(author ?? { display_name: authorName })}
      </Avatar>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0.25, sm: 1 }}
          alignItems={{ xs: 'flex-start', sm: 'baseline' }}
        >
          <Typography variant="subtitle2" sx={{ color: '#0f172a' }}>
            {authorName}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            {formatTaskCommentCreatedAt(comment)}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {canEdit && !isEditing && (
            <Tooltip title="Edit comment">
              <IconButton
                aria-label="Edit comment"
                size="small"
                onClick={onStartEdit}
                sx={{
                  color: '#64748b',
                  mt: { xs: 0.5, sm: -0.5 },
                  '&:hover': { color: '#0f172a', bgcolor: '#e2e8f0' },
                }}
              >
                <Iconify icon="eva:edit-2-outline" width={18} height={18} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        {isEditing ? (
          <Stack spacing={1} sx={{ mt: 1 }}>
            <TextField
              value={editText}
              onChange={handleEditTextChange}
              inputRef={editInputRef}
              placeholder="Edit comment..."
              multiline
              minRows={2}
              fullWidth
              disabled={isSaving}
              error={showRequiredError}
              helperText={showRequiredError ? 'Comment is required' : ''}
              inputProps={{
                'aria-label': 'Edited comment',
                onClick: handleEditTextSelect,
                onKeyUp: handleEditTextSelect,
                onSelect: handleEditTextSelect,
              }}
              sx={{
                ...fieldSx,
                '& .MuiInputBase-input': {
                  fontSize: 15,
                },
              }}
            />
            {showEditMentionSuggestions && (
              <TaskMentionSuggestions
                users={filteredEditMentionUsers}
                onSelect={handleSelectEditMentionUser}
              />
            )}
            {Boolean(editError) && (
              <Alert severity="error">
                {getErrorMessage(editError) ||
                  'There was an error while updating the comment'}
              </Alert>
            )}
            <Stack direction="row" spacing={0.75} justifyContent="flex-end">
              <Tooltip title="Cancel comment edit">
                <span>
                  <IconButton
                    aria-label="Cancel comment edit"
                    size="small"
                    disabled={isSaving}
                    onClick={onCancelEdit}
                    sx={{
                      color: '#64748b',
                      '&:hover': { color: '#0f172a', bgcolor: '#e2e8f0' },
                    }}
                  >
                    <Iconify icon="eva:close-fill" width={18} height={18} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Save comment">
                <span>
                  <LoadingButton
                    aria-label="Save comment"
                    size="small"
                    loading={isSaving}
                    disabled={!trimmedEditText || isSaving}
                    onClick={onSaveEdit}
                    sx={{
                      minWidth: 34,
                      width: 34,
                      height: 34,
                      borderRadius: 1,
                      color: '#ffffff',
                      bgcolor: '#0f172a',
                      '&:hover': { bgcolor: '#1e293b' },
                      '&.Mui-disabled': {
                        bgcolor: '#cbd5e1',
                        color: '#ffffff',
                      },
                    }}
                  >
                    <Iconify icon="eva:checkmark-fill" width={18} height={18} />
                  </LoadingButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: '#334155',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              mt: 0.35,
            }}
          >
            {commentTextParts.map((part, index) =>
              part.mention ? (
                <Box
                  key={`${part.text}-${index}`}
                  component="span"
                  data-testid="task-comment-mention"
                  sx={{ color: '#2563eb', fontWeight: 800 }}
                >
                  {part.text}
                </Box>
              ) : (
                part.text
              )
            )}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function FieldLabel({
  children,
  htmlFor,
}: {
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <Typography
      component={htmlFor ? 'label' : 'div'}
      htmlFor={htmlFor}
      variant="subtitle2"
      sx={{ color: '#64748b', fontWeight: 900, letterSpacing: 0 }}
    >
      {children}
    </Typography>
  );
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.25,
    bgcolor: '#ffffff',
  },
  '& .MuiInputBase-input': {
    fontSize: 18,
  },
};

type TaskAttachmentItemProps = {
  attachment: TaskAttachment;
  isDeleting: boolean;
  onDelete: VoidFunction;
  taskId: number;
};

function TaskAttachmentItem({
  attachment,
  isDeleting,
  onDelete,
  taskId,
}: TaskAttachmentItemProps) {
  const attachmentId = attachment.id;
  const canFetchAccessUrl =
    attachmentId !== undefined &&
    attachmentId !== null &&
    String(attachmentId).trim() !== '';
  const accessUrlQuery = useQuery({
    enabled: canFetchAccessUrl,
    queryFn: () =>
      getTaskAttachmentAccessUrl(taskId, attachmentId as string | number),
    queryKey: ['tasks', 'attachmentAccessUrl', taskId, attachmentId],
    staleTime: TASK_ATTACHMENT_ACCESS_URL_STALE_TIME_MS,
  });
  const accessUrl =
    isSafeAttachmentAccessUrl(accessUrlQuery.data?.url) &&
    accessUrlQuery.data?.url.trim()
      ? accessUrlQuery.data.url.trim()
      : '';
  const isAccessUrlLoading = canFetchAccessUrl && accessUrlQuery.isLoading;
  const showImagePreview = Boolean(accessUrl) && isImageAttachment(attachment);
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const actionButtonSx = {
    width: 30,
    height: 30,
    color: '#64748b',
    border: '1px solid #e2e8f0',
    bgcolor: '#ffffff',
    '&:hover': {
      bgcolor: '#f8fafc',
      color: '#0f172a',
    },
  };

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          border: '1px solid #e2e8f0',
          borderRadius: 1.25,
          p: 0.75,
          minHeight: 74,
          width: { xs: 1, sm: 300 },
          bgcolor: '#f8fafc',
        }}
      >
        <Box
          sx={{
            width: 58,
            height: 58,
            flex: '0 0 58px',
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid #e2e8f0',
            bgcolor: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#94a3b8',
          }}
        >
          {isAccessUrlLoading ? (
            <CircularProgress size={18} />
          ) : showImagePreview ? (
            <Box
              component="button"
              type="button"
              aria-label={`Open attachment ${attachment.name} preview`}
              onClick={() => setIsImageDialogOpen(true)}
              sx={{
                width: 1,
                height: 1,
                p: 0,
                border: 0,
                cursor: 'zoom-in',
                display: 'block',
                bgcolor: 'transparent',
              }}
            >
              <Box
                component="img"
                alt={`Preview attachment ${attachment.name}`}
                src={accessUrl}
                sx={{ width: 1, height: 1, objectFit: 'cover' }}
              />
            </Box>
          ) : (
            <Iconify
              icon={
                isImageAttachment(attachment)
                  ? 'eva:image-outline'
                  : 'eva:file-text-outline'
              }
              width={26}
              height={26}
            />
          )}
        </Box>

        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography
            variant="caption"
            title={attachment.name}
            sx={{
              display: 'block',
              color: '#0f172a',
              fontWeight: 800,
              lineHeight: 1.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {attachment.name}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            {isImageAttachment(attachment) ? 'Image' : 'Attachment'}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.5}>
          {accessUrl ? (
            <Tooltip title="Download">
              <IconButton
                component="a"
                href={accessUrl}
                download={attachment.name}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                aria-label={`Download attachment ${attachment.name}`}
                sx={actionButtonSx}
              >
                <Iconify icon="eva:download-outline" width={16} height={16} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip
              title={
                isAccessUrlLoading
                  ? 'Preparing download'
                  : 'No download available'
              }
            >
              <span>
                <IconButton
                  disabled
                  size="small"
                  aria-label={`Download attachment ${attachment.name}`}
                  sx={actionButtonSx}
                >
                  <Iconify icon="eva:download-outline" width={16} height={16} />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {attachment.id !== undefined && (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                aria-label={`Delete attachment ${attachment.name}`}
                disabled={isDeleting}
                onClick={onDelete}
                sx={actionButtonSx}
              >
                <Iconify icon="eva:close-fill" width={16} height={16} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <Dialog
        fullWidth
        maxWidth="lg"
        open={isImageDialogOpen && showImagePreview}
        onClose={() => setIsImageDialogOpen(false)}
        PaperProps={{
          'aria-label': `Attachment preview ${attachment.name}`,
          sx: {
            bgcolor: 'common.black',
            color: 'common.white',
            overflow: 'hidden',
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            aria-label={`Close attachment preview ${attachment.name}`}
            onClick={() => setIsImageDialogOpen(false)}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 1,
              color: 'common.white',
              bgcolor: alpha('#000000', 0.48),
              '&:hover': { bgcolor: alpha('#000000', 0.64) },
            }}
          >
            <Iconify icon="eva:close-fill" width={22} height={22} />
          </IconButton>
          {showImagePreview && (
            <Box
              component="img"
              alt={`Full size attachment ${attachment.name}`}
              src={accessUrl}
              sx={{
                display: 'block',
                width: 1,
                maxHeight: '82vh',
                objectFit: 'contain',
                bgcolor: 'common.black',
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function TaskBoardSkeleton() {
  return (
    <Stack direction="row" spacing={2} sx={{ overflow: 'hidden' }}>
      {BOARD_SECTION_REFS.map((column) => (
        <Paper
          key={column}
          variant="outlined"
          sx={{ width: 304, flex: '0 0 304px', p: 1.5, borderRadius: 2 }}
        >
          <Skeleton height={36} />
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            <Skeleton variant="rounded" height={96} />
            <Skeleton variant="rounded" height={120} />
            <Skeleton variant="rounded" height={88} />
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}
